/**
 * Rutas del contrato de flotas (docs/flotas-drivers/api-contract.md) servidas
 * por el mock. Recibe y devuelve camelCase; el interceptor pasa el cuerpo a
 * snake_case, como el API real.
 */
import { HttpRequest } from '@angular/common/http';
import {
  REPRESENTATIVE_ROLES,
  documentUrgency as urgencyOf,
  CHALLENGE_STATUSES,
  DEMAND_CHANNELS,
  DISPATCH_STRATEGIES,
  DriverChallenge,
  FLEET_KINDS,
  FLEET_KIND_MODE,
  FleetKind,
  GeoPoint,
  SERVICE_TYPES,
  ServiceType,
  feeApplies,
  isValidPolygon,
  pointInPolygon,
  resolvePassengerFee,
  FleetVehicle,
  DOCUMENT_TYPES,
  DOCUMENT_UPLOAD_MAX_BYTES,
  DOCUMENT_UPLOAD_MIME_TYPES,
  DocumentStatus,
  DocumentType,
  EmploymentType,
  FLEET_ROLES,
  FleetMembershipStatus,
  FleetMode,
  FleetRole,
  ProfessionalDriverOnboardingStatus,
  RejectionReason,
  SHIFT_SLOTS,
  ShiftAssignment,
  ShiftSlotHours,
  UPCOMING_EXPIRATIONS_DAYS,
  WEEKDAYS,
  documentTypeInfo,
  documentTypesFor,
  documentUrgency,
  expiryStatusFor,
  findShiftConflicts,
  mostUrgent,
  urgencyRank,
} from 'src/app/models/drivers-fleet';
import { FLEET_MOCK_ROLE, fleetRolePages } from './fleet-mock.config';
import {
  FleetMockStore,
  MOCK_REVIEWER,
  MockDocument,
  MockFleet,
  MockMembership,
  MockState,
  MockVehicle,
  hashUnit,
  nowIso,
  regionsEnabled,
} from './fleet-mock.store';

export interface MockResponse {
  status: number;
  body: unknown;
  /** Respuesta binaria (exportes). */
  blob?: Blob;
  headers?: Record<string, string>;
}

/** Error con el formato del API (`message`, `error_type`, `errors`…). */
export class MockApiError extends Error {
  constructor(
    public status: number,
    public body: Record<string, unknown>,
  ) {
    super(String(body['message'] ?? 'Mock error'));
  }
}

const LEGAL_VERSION = '2026-10-09';
const DISCLAIMER_VERSION = '2026-10-14';
const DISCLAIMER_BASE = [
  'Confirmo que estos datos fiscales son correctos y autorizo a WAIIS a emitir en nombre de mi empresa las facturas de los servicios hechos con la plataforma.',
  'Entiendo que, al dar el NIF, WAIIS considera que la empresa está dada de alta en Hacienda.',
];
const DISCLAIMER_LAST = 'WAIIS no se hace responsable si los datos que doy son incorrectos.';
const DISCLAIMER_ITEMS = {
  taxi: [...DISCLAIMER_BASE, DISCLAIMER_LAST],
  vtc: [
    ...DISCLAIMER_BASE,
    'Autorizo a WAIIS a usar los datos de cada viaje (fecha, hora, origen, destino), los de la empresa y las matrículas para crear y enviar las hojas de ruta al Ministerio.',
    DISCLAIMER_LAST,
  ],
};
const LOREM =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor incididunt ut labore et dolore ' +
  'magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo ' +
  'consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.';
const REJECTION_REASONS: RejectionReason[] = [
  'illegible',
  'expired',
  'holder_mismatch',
  'plate_mismatch',
  'wrong_document',
  'other',
];

type Handler = (ctx: RouteContext) => MockResponse;

interface RouteContext {
  req: HttpRequest<unknown>;
  params: Record<string, string>;
  query: URLSearchParams;
  body: any;
}

interface Route {
  method: string;
  pattern: RegExp;
  keys: string[];
  /** Pantalla que pide en una flota (comprobación de rol del escenario). */
  page?: string;
  handler: Handler;
}

function ok(body: unknown, status = 200): MockResponse {
  return { status, body };
}

function data(value: unknown, status = 200): MockResponse {
  return ok({ data: value }, status);
}

function notFound(): never {
  throw new MockApiError(404, { message: 'Recurso no encontrado.', error_type: 'resource_not_found' });
}

function conflict(errorType: string, message: string, extra: Record<string, unknown> = {}): never {
  throw new MockApiError(409, { message, error_type: errorType, ...extra });
}

/** 422 con `errors` y `failed_rules`, como los FormRequest del backend. */
function invalid(fields: Record<string, [string, string]>, extra: Record<string, unknown> = {}): never {
  const errors: Record<string, string[]> = {};
  const failedRules: Record<string, string[]> = {};
  Object.entries(fields).forEach(([field, [message, rule]]) => {
    errors[field] = [message];
    failedRules[field] = [rule];
  });
  const first = Object.values(errors)[0]?.[0] ?? 'Datos no válidos.';
  const more = Object.keys(errors).length - 1;
  throw new MockApiError(422, {
    message: more > 0 ? `${first} (y ${more} más)` : first,
    errors,
    failed_rules: failedRules,
    ...extra,
  });
}

function paginate<T>(items: T[], query: URLSearchParams, counts?: Record<string, number>, extraMeta: Record<string, unknown> = {}): MockResponse {
  const perPage = Math.min(Math.max(Number(query.get('per_page')) || 20, 1), 100);
  const total = items.length;
  const lastPage = Math.max(Math.ceil(total / perPage), 1);
  const currentPage = Math.min(Math.max(Number(query.get('page')) || 1, 1), lastPage);
  const start = (currentPage - 1) * perPage;
  const slice = items.slice(start, start + perPage);
  return ok({
    data: slice,
    meta: {
      currentPage,
      lastPage,
      perPage,
      total,
      from: slice.length ? start + 1 : null,
      to: slice.length ? start + slice.length : null,
      ...(counts ? { counts } : {}),
      ...extraMeta,
    },
  });
}

function includesText(haystack: (string | null | undefined)[], needle: string | null): boolean {
  if (!needle) {
    return true;
  }
  const n = normalize(needle);
  return haystack.some((h) => normalize(h ?? '').includes(n));
}

function normalize(text: string): string {
  return text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

function normalizePlate(plate: string): string {
  return plate.toUpperCase().replace(/[\s-]/g, '');
}

function countBy<T>(items: T[], key: (item: T) => string): Record<string, number> {
  return items.reduce<Record<string, number>>((acc, item) => {
    const k = key(item);
    acc[k] = (acc[k] ?? 0) + 1;
    return acc;
  }, {});
}

function boolParam(value: string | null): boolean | null {
  if (value === null || value === '') {
    return null;
  }
  return value === '1' || value === 'true';
}

function dateOnly(iso: string): string {
  return iso.slice(0, 10);
}


/** Peaje de un viaje al aeropuerto y cancelación cobrada (extracto del mock). */
const TOLL_CENTS = 460;
const CANCELLATION_FEE_CENTS = 345;

export class FleetMockApi {
  private readonly routes: Route[] = [];

  constructor(private store: FleetMockStore) {
    this.registerRoutes();
  }

  /** Respuesta del mock para la petición, o `null` si no es una ruta del contrato. */
  handle(req: HttpRequest<unknown>): MockResponse | null {
    const path = backofficePath(req.url);
    if (path === null) {
      return null;
    }
    for (const route of this.routes) {
      if (route.method !== req.method) {
        continue;
      }
      const match = route.pattern.exec(path);
      if (!match) {
        continue;
      }
      const params: Record<string, string> = {};
      route.keys.forEach((key, index) => (params[key] = decodeURIComponent(match[index + 1])));
      if (route.page && params['fleetId']) {
        this.assertFleetPage(params['fleetId'], route.page);
      }
      const query = new URLSearchParams(req.urlWithParams.split('?')[1] ?? '');
      const response = route.handler({ req, params, query, body: req.body });
      this.store.save();
      return response;
    }
    return null;
  }

  /** ¿Es una ruta de este contrato (aunque el método no exista)? */
  owns(url: string): boolean {
    const path = backofficePath(url);
    return path !== null && /^\/(fleets|fleet-catalogs|document-reviews|professional-drivers|passenger-fees|driver-challenges)(\/|$)/.test(path);
  }

  // ---------------------------------------------------------------- registro

  private on(method: string, path: string, handler: Handler, page?: string): void {
    const keys: string[] = [];
    const pattern = new RegExp(
      '^' +
        path.replace(/:([a-zA-Z]+)/g, (_, key: string) => {
          keys.push(key);
          return '([^/]+)';
        }) +
        '$',
    );
    this.routes.push({ method, pattern, keys, page, handler });
  }

  /** El rol del escenario solo ve las pantallas de su rol en la flota del titular. */
  private assertFleetPage(fleetId: string, page: string): void {
    const fleet = this.store.fleet(fleetId);
    if (!fleet) {
      notFound();
    }
    if (!fleet.isScenarioFleet) {
      return; // Flotas de ejemplo: se entra como admin.
    }
    const role = FLEET_MOCK_ROLE[this.store.settings.role];
    if (!fleetRolePages(role, fleet.kind).includes(page)) {
      throw new MockApiError(403, {
        message: 'No tienes permiso para esta operación.',
        error_type: 'action_unauthorized',
        operation_id: `backoffice.fleets.${page}`,
      });
    }
  }

  private registerRoutes(): void {
    // Catálogos
    this.on('GET', '/fleet-catalogs', () => this.catalogs());

    // Admin: listado de flotas y alta
    this.on('GET', '/fleets', (ctx) => this.listFleets(ctx));
    this.on('POST', '/fleets', (ctx) => this.createFleet(ctx));

    // Flota
    this.on('GET', '/fleets/:fleetId', (ctx) => data(this.store.toFleet(this.fleetOr404(ctx))), 'fleet_overview');
    this.on('PATCH', '/fleets/:fleetId', (ctx) => this.updateFleet(ctx), 'fleet_settings');
    this.on('PUT', '/fleets/:fleetId/billing', (ctx) => this.updateBilling(ctx), 'fleet_settings');
    this.on('POST', '/fleets/:fleetId/documents', (ctx) => this.uploadFleetDocument(ctx), 'fleet_documents');
    this.on('POST', '/fleets/:fleetId/submit', (ctx) => this.submitFleet(ctx), 'fleet_settings');
    // Admin: revisión de los datos de facturación (paso billing_info).
    this.on('POST', '/fleets/:fleetId/billing/approve', (ctx) => this.reviewBilling(ctx, 'approved'));
    this.on('POST', '/fleets/:fleetId/billing/reject', (ctx) => this.reviewBilling(ctx, 'rejected'));
    this.on('POST', '/fleets/:fleetId/fleet-code/regenerate', (ctx) => this.regenerateCode(ctx), 'fleet_drivers');
    this.on('GET', '/fleets/:fleetId/overview', (ctx) => this.overview(ctx), 'fleet_overview');
    this.on('GET', '/fleets/:fleetId/presence', (ctx) => this.presence(ctx), 'fleet_overview');
    this.on('PUT', '/fleets/:fleetId/rules', (ctx) => this.updateRules(ctx), 'fleet_settings');
    this.on('PUT', '/fleets/:fleetId/bank-account', (ctx) => this.updateBankAccount(ctx), 'fleet_settings');

    // Conductores
    this.on('GET', '/fleets/:fleetId/drivers', (ctx) => this.listDrivers(ctx), 'fleet_drivers');
    this.on('GET', '/fleets/:fleetId/drivers/:membershipId', (ctx) => this.showDriver(ctx), 'fleet_drivers');
    this.on('POST', '/fleets/:fleetId/drivers/:membershipId/accept', (ctx) => this.acceptDriver(ctx), 'fleet_drivers');
    this.on('POST', '/fleets/:fleetId/drivers/:membershipId/reject', (ctx) => this.rejectDriver(ctx), 'fleet_drivers');
    this.on('PATCH', '/fleets/:fleetId/drivers/:membershipId', (ctx) => this.updateDriver(ctx), 'fleet_drivers');
    this.on('DELETE', '/fleets/:fleetId/drivers/:membershipId', (ctx) => this.removeDriver(ctx), 'fleet_drivers');

    // Vehículos
    this.on('GET', '/fleets/:fleetId/vehicles', (ctx) => this.listVehicles(ctx), 'fleet_vehicles');
    this.on('POST', '/fleets/:fleetId/vehicles', (ctx) => this.createVehicle(ctx), 'fleet_vehicles');
    this.on('GET', '/fleets/:fleetId/vehicles/:vehicleId', (ctx) => this.showVehicle(ctx), 'fleet_vehicles');
    this.on('PATCH', '/fleets/:fleetId/vehicles/:vehicleId', (ctx) => this.updateVehicle(ctx), 'fleet_vehicles');
    this.on('DELETE', '/fleets/:fleetId/vehicles/:vehicleId', (ctx) => this.deleteVehicle(ctx), 'fleet_vehicles');
    this.on('POST', '/fleets/:fleetId/vehicles/:vehicleId/submit', (ctx) => this.submitVehicle(ctx), 'fleet_vehicles');
    this.on('POST', '/fleets/:fleetId/vehicles/:vehicleId/documents', (ctx) => this.uploadVehicleDocument(ctx), 'fleet_vehicles');

    // Turnos
    this.on('GET', '/fleets/:fleetId/shift-slots', (ctx) => data(this.store.state.shiftSlots[this.fleetOr404(ctx).id] ?? []), 'fleet_shifts');
    this.on('PUT', '/fleets/:fleetId/shift-slots', (ctx) => this.updateShiftSlots(ctx), 'fleet_settings');
    this.on('GET', '/fleets/:fleetId/shifts', (ctx) => data(this.store.state.shifts[this.fleetOr404(ctx).id]), 'fleet_shifts');
    this.on('PUT', '/fleets/:fleetId/shifts', (ctx) => this.updateShifts(ctx), 'fleet_shifts');

    // Documentos
    this.on('GET', '/fleets/:fleetId/documents/matrix', (ctx) => this.documentsMatrix(ctx), 'fleet_documents');
    this.on('GET', '/fleets/:fleetId/documents/:documentId', (ctx) => this.showFleetDocument(ctx), 'fleet_documents');

    // Liquidaciones, rendimiento e informes
    this.on('GET', '/fleets/:fleetId/settlements/summary', (ctx) => this.settlementsSummary(ctx), 'fleet_settlements');
    this.on('GET', '/fleets/:fleetId/settlements/statement', (ctx) => this.statement(ctx), 'fleet_settlements');
    this.on('GET', '/fleets/:fleetId/settlements/movements', (ctx) => this.settlementMovements(ctx), 'fleet_settlements');
    this.on('PUT', '/fleets/:fleetId/settlements/settings', (ctx) => this.updateSettlementSettings(ctx), 'fleet_settlements');
    this.on('GET', '/fleets/:fleetId/performance/drivers', (ctx) => this.driversPerformance(ctx), 'fleet_performance');
    this.on('GET', '/fleets/:fleetId/performance/summary', (ctx) => this.performanceSummary(ctx), 'fleet_performance');
    this.on('GET', '/fleets/:fleetId/performance/demand-by-hour', (ctx) => this.demandByHour(ctx), 'fleet_performance');
    this.on('GET', '/fleets/:fleetId/reports/months', (ctx) => this.reportMonths(ctx), 'fleet_reports');
    this.on('GET', '/fleets/:fleetId/accounting-settings', (ctx) => data({ ...this.fleetOr404(ctx).accounting }), 'fleet_reports');
    this.on('PUT', '/fleets/:fleetId/accounting-settings', (ctx) => this.updateAccounting(ctx), 'fleet_reports');
    this.on('GET', '/fleets/:fleetId/reports/:report/export', (ctx) => this.exportReport(ctx), 'fleet_reports');

    // Equipo y ajustes
    this.on('GET', '/fleets/:fleetId/members', (ctx) => this.listMembers(ctx), 'fleet_team');
    this.on('POST', '/fleets/:fleetId/members', (ctx) => this.inviteMember(ctx), 'fleet_team');
    this.on('PATCH', '/fleets/:fleetId/members/:memberId', (ctx) => this.updateMember(ctx), 'fleet_team');
    this.on('DELETE', '/fleets/:fleetId/members/:memberId', (ctx) => this.removeMember(ctx), 'fleet_team');
    this.on('POST', '/fleets/:fleetId/members/:memberId/resend', (ctx) => this.resendMember(ctx), 'fleet_team');
    this.on('GET', '/fleets/:fleetId/notification-settings', (ctx) => data(this.store.state.notifications[this.fleetOr404(ctx).id]), 'fleet_settings');
    this.on('PUT', '/fleets/:fleetId/notification-settings', (ctx) => this.updateNotifications(ctx), 'fleet_settings');

    // Emisora: paradas, zonas y política de reparto (fase 4a)
    this.on('GET', '/fleets/:fleetId/stands', (ctx) => this.listStands(ctx), 'fleet_stands');
    this.on('POST', '/fleets/:fleetId/stands', (ctx) => this.saveStand(ctx), 'fleet_stands');
    this.on('PATCH', '/fleets/:fleetId/stands/:standId', (ctx) => this.saveStand(ctx), 'fleet_stands');
    this.on('DELETE', '/fleets/:fleetId/stands/:standId', (ctx) => this.deleteStand(ctx), 'fleet_stands');
    this.on('GET', '/fleets/:fleetId/stands/:standId/queue', (ctx) => this.standQueue(ctx), 'fleet_stands');
    this.on('POST', '/fleets/:fleetId/stands/:standId/queue/:membershipId/remove', (ctx) => this.removeFromQueue(ctx), 'fleet_stands');
    this.on('GET', '/fleets/:fleetId/zones', (ctx) => this.listZones(ctx), 'fleet_zones');
    this.on('POST', '/fleets/:fleetId/zones', (ctx) => this.saveZone(ctx), 'fleet_zones');
    this.on('PATCH', '/fleets/:fleetId/zones/:zoneId', (ctx) => this.saveZone(ctx), 'fleet_zones');
    this.on('DELETE', '/fleets/:fleetId/zones/:zoneId', (ctx) => this.deleteZone(ctx), 'fleet_zones');
    this.on('GET', '/fleets/:fleetId/dispatch-policy', (ctx) => data({ ...this.policyOf(this.dispatchCenterOr409(ctx)) }), 'fleet_dispatch_policy');
    this.on('PUT', '/fleets/:fleetId/dispatch-policy', (ctx) => this.updatePolicy(ctx), 'fleet_dispatch_policy');
    this.on('GET', '/fleets/:fleetId/dispatch-policy/history', (ctx) => paginate(this.store.state.policyHistory[this.dispatchCenterOr409(ctx).id] ?? [], ctx.query), 'fleet_dispatch_policy');

    // Admin: cuota al pasajero y retos (fase 4a)
    this.on('GET', '/passenger-fees', () => data({ rules: this.sortedRules(), regions: this.store.state.feeRegions }));
    this.on('POST', '/passenger-fees/rules', (ctx) => this.saveFeeRule(ctx));
    this.on('PATCH', '/passenger-fees/rules/:ruleId', (ctx) => this.saveFeeRule(ctx));
    this.on('DELETE', '/passenger-fees/rules/:ruleId', (ctx) => this.deleteFeeRule(ctx));
    this.on('PUT', '/passenger-fees/regions/:regionId', (ctx) => this.toggleFeeRegion(ctx));
    this.on('POST', '/passenger-fees/simulate', (ctx) => this.simulateFee(ctx));
    this.on('GET', '/passenger-fees/history', (ctx) => paginate([...this.store.state.feeHistory].sort((a, b) => b.at.localeCompare(a.at)), ctx.query));
    this.on('GET', '/driver-challenges', (ctx) => this.listChallenges(ctx));
    this.on('POST', '/driver-challenges', (ctx) => this.saveChallenge(ctx));
    this.on('GET', '/driver-challenges/:challengeId', (ctx) => data(this.challengeOr404(ctx)));
    this.on('PATCH', '/driver-challenges/:challengeId', (ctx) => this.saveChallenge(ctx));
    this.on('DELETE', '/driver-challenges/:challengeId', (ctx) => this.deleteChallenge(ctx));
    this.on('POST', '/driver-challenges/:challengeId/publish', (ctx) => this.publishChallenge(ctx));
    this.on('POST', '/driver-challenges/:challengeId/cancel', (ctx) => this.cancelChallenge(ctx));
    this.on('GET', '/driver-challenges/:challengeId/participants', (ctx) =>
      paginate(this.store.state.challengeParticipants[this.challengeOr404(ctx).id] ?? [], ctx.query),
    );

    // Admin: verificación de documentos y conductores profesionales
    this.on('GET', '/document-reviews', (ctx) => this.listReviews(ctx));
    this.on('GET', '/document-reviews/:documentId', (ctx) => data(this.store.reviewDetail(this.reviewableOr404(ctx))));
    this.on('POST', '/document-reviews/:documentId/approve', (ctx) => this.approveDocument(ctx));
    this.on('POST', '/document-reviews/:documentId/reject', (ctx) => this.rejectDocument(ctx));
    this.on('GET', '/professional-drivers', (ctx) => this.listProfessionalDrivers(ctx));
    this.on('GET', '/professional-drivers/:userId', (ctx) => this.showProfessionalDriver(ctx));
  }

  // ---------------------------------------------------------------- comunes

  private fleetOr404(ctx: RouteContext): MockFleet {
    return this.store.fleet(ctx.params['fleetId']) ?? notFound();
  }

  private membershipOr404(ctx: RouteContext, fleet: MockFleet): MockMembership {
    const membership = this.store.state.memberships.find(
      (m) => m.membershipId === ctx.params['membershipId'] && m.fleetId === fleet.id && m.status !== 'removed',
    );
    return membership ?? notFound();
  }

  private uploader(fleet: MockFleet): { id: string; name: string; kind: 'fleet' } {
    const owner = this.store.state.members.find((m) => m.fleetId === fleet.id && m.role === 'fleet_owner');
    return { id: owner?.user?.id ?? 'owner', name: owner?.user?.name ?? 'Titular', kind: 'fleet' };
  }

  /** Lee `type` + `file` de un multipart y valida tipo y fichero. */
  private readUpload(ctx: RouteContext, allowed: DocumentType[]): { type: DocumentType; file: File | null } {
    const form = ctx.body instanceof FormData ? ctx.body : null;
    const type = (form?.get('type') as DocumentType | null) ?? (ctx.body?.type as DocumentType | undefined) ?? null;
    const file = (form?.get('file') as File | null) ?? null;
    if (!type || !allowed.includes(type)) {
      invalid({ type: ['El tipo de documento no corresponde a este dueño o a este modo.', 'In'] });
    }
    if (file) {
      if (file.size > DOCUMENT_UPLOAD_MAX_BYTES) {
        invalid({ file: ['El archivo no puede superar 30 MB.', 'Max'] });
      }
      if (file.type && !DOCUMENT_UPLOAD_MIME_TYPES.includes(file.type)) {
        invalid({ file: ['El archivo debe ser JPG, PNG o PDF.', 'Mimes'] });
      }
    } else {
      invalid({ file: ['El archivo es obligatorio.', 'Required'] });
    }
    return { type, file };
  }

  // ---------------------------------------------------------------- catálogos

  private catalogs(): MockResponse {
    return data({
      regions: this.store.state.regions,
      waiisTaxId: 'A12345678',
      demandChannels: DEMAND_CHANNELS.map((channel) => ({ channel, feeApplies: feeApplies(channel) })),
      serviceTypes: SERVICE_TYPES,
      legalTexts: {
        fleetTerms: { version: LEGAL_VERSION, body: LOREM },
        selfBillingAgreement: { version: LEGAL_VERSION, body: LOREM },
        billingDisclaimer: { version: DISCLAIMER_VERSION, items: DISCLAIMER_ITEMS },
      },
      documentTypes: DOCUMENT_TYPES,
      rejectionReasons: REJECTION_REASONS,
      vatRegimes: ['general', 'simplified', 'exempt'],
    });
  }

  // ---------------------------------------------------------------- alta y flota

  private listFleets(ctx: RouteContext): MockResponse {
    const { query } = ctx;
    // Contadores por estado con el resto de filtros aplicados (para las pestañas).
    const scoped = this.store.state.fleets
      .map((f) => this.store.toFleet(f, false))
      .filter(
        (f) =>
          (!query.get('mode') || f.mode === query.get('mode')) &&
          (!query.get('kind') || f.kind === query.get('kind')) &&
          (!query.get('region_id') || f.region?.id === query.get('region_id')) &&
          includesText([f.name, f.contactEmail, f.fleetCode], query.get('search')),
      );
    const counts = countBy(scoped, (f) => f.status);
    const fleets = scoped.filter((f) => !query.get('status') || f.status === query.get('status'));
    return paginate(fleets.sort((a, b) => b.createdAt.localeCompare(a.createdAt)), query, counts);
  }

  private createFleet(ctx: RouteContext): MockResponse {
    const body = ctx.body ?? {};
    const errors: Record<string, [string, string]> = {};
    if (!FLEET_KINDS.includes(body.kind)) errors['kind'] = ['Elige emisora, taxista patrón o flota VTC.', 'In'];
    if (!this.store.state.regions.some((r) => r.id === body.regionId)) errors['region_id'] = ['La región no existe o no está activa.', 'Exists'];
    if (!/^\S+@\S+\.\S+$/.test(body.contactEmail ?? '')) errors['contact_email'] = ['El correo no es válido.', 'Email'];
    if (!String(body.name ?? '').trim()) errors['name'] = ['El nombre es obligatorio.', 'Required'];
    if (Object.keys(errors).length) invalid(errors);

    const kind = body.kind as FleetKind;
    const mode = FLEET_KIND_MODE[kind];
    // Solo puede haber una flota del titular: la nueva sustituye a la del escenario.
    this.store.state.fleets.forEach((f) => (f.isScenarioFleet = false));
    const fleetId = this.store.newId();
    const fleet: MockFleet = {
      id: fleetId,
      name: String(body.name).trim(),
      kind,
      mode,
      regionId: body.regionId,
      contactEmail: body.contactEmail,
      fleetCode: `${normalize(body.name).replace(/[^a-z]/g, '').toUpperCase().slice(0, 6) || 'FLOTA'}-${1000 + Math.floor(Math.random() * 8999)}`,
      billing: null,
      billingStatus: 'pending',
      billingRejection: null,
      legalAcceptance: null,
      submittedAt: null,
      approvedAt: null,
      createdAt: nowIso(),
      defaultRevenueSharePercentage: 55,
      isScenarioFleet: true,
      ibanMasked: null,
      rules: { expiryReminders: true, blockOnExpiry: true, acceptCash: false, cancellationAlert: true },
      accounting: { monthlyEmailEnabled: false, email: null },
    };
    this.store.state.fleets.push(fleet);
    this.store.state.shiftSlots[fleetId] = [
      { slot: 'morning', start: '06:00', end: '14:00' },
      { slot: 'afternoon', start: '14:00', end: '22:00' },
      { slot: 'night', start: '22:00', end: '06:00' },
    ];
    this.store.state.shifts[fleetId] = { assignments: [], updatedAt: null };
    this.store.state.notifications[fleetId] = { documentExpiring: true, documentRejected: true, driverRequests: true, weeklySettlement: false };
    this.store.state.movements[fleetId] = [];
    this.store.state.members.push({
      id: this.store.newId(),
      fleetId,
      email: body.contactEmail,
      name: 'Tú',
      user: { id: 'me', name: 'Tú' },
      role: 'fleet_owner',
      status: 'active',
      invitedAt: nowIso(),
      lastSeenAt: nowIso(),
    });
    if (kind === 'dispatch_center') {
      this.store.state.dispatchPolicies[fleetId] = {
        strategy: 'proximity', intraFleetPriority: true, offerTimeoutSeconds: 20,
        standMaxConsecutiveRejections: 2, skipByRequirementsKeepsPosition: true, updatedAt: null, updatedBy: null,
      };
      this.store.state.policyHistory[fleetId] = [];
    }
    // A partir de aquí `/me` trae el rol de titular de esta flota (ver el interceptor).
    return data(this.store.toFleet(fleet), 201);
  }

  private updateFleet(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const body = ctx.body ?? {};
    if ('mode' in body || 'kind' in body) {
      invalid({ kind: ['El tipo de la flota no se puede cambiar.', 'Prohibited'] });
    }
    if (body.name !== undefined) {
      if (!String(body.name).trim()) invalid({ name: ['El nombre es obligatorio.', 'Required'] });
      fleet.name = String(body.name).trim();
    }
    if (body.contactEmail !== undefined) {
      if (!/^\S+@\S+\.\S+$/.test(body.contactEmail)) invalid({ contact_email: ['El correo no es válido.', 'Email'] });
      fleet.contactEmail = body.contactEmail;
    }
    return data(this.store.toFleet(fleet));
  }

  private updateBilling(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const b = ctx.body ?? {};
    const errors: Record<string, [string, string]> = {};
    if (!['company', 'self_employed'].includes(b.entityType)) errors['entity_type'] = ['Elige empresa o autónomo.', 'In'];
    if (!String(b.legalName ?? '').trim()) errors['legal_name'] = ['La razón social es obligatoria.', 'Required'];
    if (!/^[A-Z0-9]{9}$/i.test(String(b.taxId ?? '').replace(/[\s-]/g, ''))) errors['tax_id'] = ['El NIF no es válido.', 'Regex'];
    if (!String(b.address?.line ?? '').trim()) errors['address.line'] = ['La dirección es obligatoria.', 'Required'];
    if (!/^\d{5}$/.test(String(b.address?.postalCode ?? ''))) errors['address.postal_code'] = ['El código postal no es válido.', 'Regex'];
    if (!String(b.vatRegime ?? '').trim()) errors['vat_regime'] = ['Elige el régimen de IVA.', 'Required'];
    // Mockup 28: representante, socios y descargo. Opcionales hasta que el alta
    // nueva (R9) los mande; si llegan, se validan como el backend.
    if (b.representative && !REPRESENTATIVE_ROLES.includes(b.representative.role)) {
      errors['representative.role'] = ['Elige tu cargo en la empresa.', 'In'];
    }
    const owners = (Array.isArray(b.beneficialOwners) ? b.beneficialOwners : []).filter((o: any) => String(o?.name ?? '').trim());
    if (b.representative && b.representative.role !== 'sole_admin' && !owners.length) {
      errors['beneficial_owners'] = ['Indica los socios con más del 25 %.', 'RequiredUnless'];
    }
    if (owners.some((o: any) => !/^[0-9XYZ][0-9]{7}[A-Z]$/i.test(String(o.idNumber ?? '').replace(/[\s-]/g, '')))) {
      errors['beneficial_owners'] = ['Algún DNI o NIE no es válido.', 'Regex'];
    }
    if (Object.keys(errors).length) invalid(errors);
    if (b.disclaimerVersion !== undefined && b.disclaimerVersion !== DISCLAIMER_VERSION) {
      conflict('legal_texts_outdated', 'El descargo ha cambiado. Revísalo y acéptalo de nuevo.');
    }
    fleet.billing = {
      entityType: b.entityType,
      legalName: String(b.legalName).trim(),
      taxId: String(b.taxId).toUpperCase().replace(/[\s-]/g, ''),
      address: {
        line: b.address.line,
        postalCode: b.address.postalCode,
        city: b.address.city ?? '',
        province: b.address.province ?? '',
        country: b.address.country ?? 'ES',
      },
      vatRegime: b.vatRegime,
      representative: b.representative ? { role: b.representative.role, name: b.representative.name ?? null } : null,
      beneficialOwners: owners.map((o: any) => ({ name: String(o.name).trim(), idNumber: String(o.idNumber).toUpperCase() })),
      expectedFleetSize: b.expectedFleetSize ?? null,
      disclaimerVersion: b.disclaimerVersion ?? null,
    };
    fleet.billingStatus = 'in_review';
    fleet.billingRejection = null;
    return data(this.store.toFleet(fleet));
  }

  private uploadFleetDocument(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const { type, file } = this.readUpload(ctx, documentTypesFor('fleet', fleet.mode));
    const doc = this.store.addDocumentVersion(
      'fleet',
      fleet.id,
      type,
      { mode: fleet.mode, fleetId: fleet.id, driverUserId: null },
      this.uploader(fleet),
      file,
    );
    return data(this.store.documentForFleet(doc), 201);
  }

  private submitFleet(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    // Sin cuerpo desde el mockup 28 (el descargo va en el paso fiscal); las
    // versiones de las fases 0-3 se aceptan y se ignoran.
    const missing = this.store
      .fleetSteps(fleet)
      .filter((s) => s.status === 'pending')
      .map((s) => s.key);
    if (missing.length) {
      conflict('fleet_onboarding_incomplete', 'Faltan pasos por completar.', { missing_steps: missing });
    }
    fleet.legalAcceptance = {
      fleetTermsVersion: LEGAL_VERSION,
      selfBillingAgreementVersion: LEGAL_VERSION,
      acceptedAt: nowIso(),
    };
    fleet.submittedAt = fleet.submittedAt ?? nowIso();
    return data(this.store.toFleet(fleet));
  }

  private reviewBilling(ctx: RouteContext, decision: 'approved' | 'rejected'): MockResponse {
    const fleet = this.fleetOr404(ctx);
    if (!fleet.submittedAt || !fleet.billing || fleet.billingStatus !== 'in_review') {
      conflict('billing_not_in_review', 'Los datos de facturación no están pendientes de revisión.');
    }
    if (decision === 'rejected') {
      const reason = ctx.body?.reason as RejectionReason;
      const comment = String(ctx.body?.comment ?? '').trim() || null;
      if (!REJECTION_REASONS.includes(reason)) invalid({ reason: ['Elige un motivo de rechazo.', 'In'] });
      if (reason === 'other' && !comment) invalid({ comment: ['Explica el motivo.', 'RequiredIf'] });
      fleet.billingRejection = { reason, comment };
    } else {
      fleet.billingRejection = null;
    }
    fleet.billingStatus = decision;
    if (this.store.fleetStatus(fleet) === 'approved') {
      fleet.approvedAt = nowIso();
    }
    return data(this.store.toFleet(fleet));
  }

  private regenerateCode(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const prefix = fleet.fleetCode.split('-')[0];
    fleet.fleetCode = `${prefix}-${1000 + Math.floor(Math.random() * 8999)}`;
    return data({ fleetCode: fleet.fleetCode });
  }

  private overview(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const rangeDays = [7, 14, 30, 90].includes(Number(ctx.query.get('range_days'))) ? Number(ctx.query.get('range_days')) : 14;
    const approved = this.store.fleetStatus(fleet) === 'approved';
    const now = Date.now();
    const inRange = (iso: string, fromDays: number, toDays: number) => {
      const age = (now - new Date(iso).getTime()) / 86_400_000;
      return age >= fromDays && age < toDays;
    };
    const trips = (this.store.state.movements[fleet.id] ?? []).filter((m) => m.type === 'trip_earning');

    // El periodo son exactamente los días de la gráfica (uno por día).
    const byDay = new Map<string, number>();
    for (let d = rangeDays - 1; d >= 0; d--) {
      byDay.set(dateOnly(new Date(now - d * 86_400_000).toISOString()), 0);
    }
    const current = approved ? trips.filter((m) => byDay.has(dateOnly(m.occurredAt))) : [];
    const previous = approved
      ? trips.filter((m) => !byDay.has(dateOnly(m.occurredAt)) && inRange(m.occurredAt, rangeDays - 1, rangeDays * 2))
      : [];
    current.forEach((m) => byDay.set(dateOnly(m.occurredAt), (byDay.get(dateOnly(m.occurredAt)) ?? 0) + 1));

    const byDriver = new Map<string, { membershipId: string; name: string; trips: number; fareCents: number; hoursOnline: number }>();
    current.forEach((m) => {
      if (!m.driver) return;
      const row = byDriver.get(m.driver.membershipId) ?? { ...m.driver, trips: 0, fareCents: 0, hoursOnline: 0 };
      row.trips += 1;
      row.fareCents += m.amountCents;
      row.hoursOnline = Math.round(row.trips * (1.4 + hashUnit(m.driver.membershipId)));
      byDriver.set(m.driver.membershipId, row);
    });

    const vehicles = this.store.fleetVehicles(fleet.id).map((v) => this.store.toVehicle(v, fleet.mode));
    const docs = this.allFleetDocuments(fleet);
    const accepted = this.store.fleetMemberships(fleet.id, ['accepted']);
    // En una emisora cada socio lleva su coche: no hay «conductor sin coche».
    const withoutVehicle = fleet.kind === 'dispatch_center' ? [] : accepted.filter((m) => !this.store.driverVehicle(m));
    return data({
      kpis: {
        driversActive: approved ? accepted.filter((m) => this.store.toFleetDriver(m).canWork).length : 0,
        vehiclesWorking: approved ? vehicles.filter((v) => v.canWork).length : 0,
        tripsCompleted: current.length,
        fareTotalCents: current.reduce((sum, m) => sum + m.amountCents, 0),
        feeTotalCents: current.reduce((sum, m) => sum + (m.trip?.feeCents ?? 0), 0),
      },
      previous: {
        tripsCompleted: previous.length,
        fareTotalCents: previous.reduce((sum, m) => sum + m.amountCents, 0),
      },
      tripsByDay: [...byDay.entries()].map(([date, count]) => ({ date, trips: count })),
      topDrivers: [...byDriver.values()].sort((a, b) => b.trips - a.trips || b.fareCents - a.fareCents).slice(0, 4),
      alerts: {
        documentsExpired: docs.filter((d) => d.status === 'approved' && expiryStatusFor(d.expiresAt) === 'expired').length,
        documentsExpiringSoon: docs.filter((d) => d.status === 'approved' && expiryStatusFor(d.expiresAt) === 'expiring_soon').length,
        documentsRejected: docs.filter((d) => d.status === 'rejected').length,
        driverRequestsPending: this.store.fleetMemberships(fleet.id, ['pending']).length,
        driversWithoutVehicle: withoutVehicle.length,
      },
      week: approved ? this.overviewWeek(fleet) : this.emptyWeek(),
      attention: this.attention(fleet, docs, withoutVehicle),
      fleet: {
        vehiclesByStatus: {
          incomplete: vehicles.filter((v) => v.status === 'incomplete').length,
          in_review: vehicles.filter((v) => v.status === 'in_review').length,
          rejected: vehicles.filter((v) => v.status === 'rejected').length,
          approved: vehicles.filter((v) => v.status === 'approved').length,
        },
        drivers: {
          ready: accepted.filter((m) => this.store.driverDocsOk(this.store.driver(m.userId)!)).length,
          verifying: accepted.filter((m) => !this.store.driverDocsOk(this.store.driver(m.userId)!)).length,
          pending: this.store.fleetMemberships(fleet.id, ['pending']).length,
        },
      },
    });
  }

  /** Documentos vigentes de la flota, sus vehículos y sus conductores aceptados. */
  private allFleetDocuments(fleet: MockFleet): MockDocument[] {
    const vehicleDocs = this.store.fleetVehicles(fleet.id).flatMap((v) => this.store.vehicleDocuments(v, fleet.mode));
    const driverDocs = this.store
      .fleetMemberships(fleet.id, ['accepted'])
      .flatMap((m) => this.store.driverDocuments(this.store.driver(m.userId)!));
    return [...this.store.currentDocuments('fleet', fleet.id), ...vehicleDocs, ...driverDocs];
  }

  // ---------------------------------------------------------------- conductores

  private listDrivers(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const { query } = ctx;
    const all = this.store
      .fleetMemberships(fleet.id, ['pending', 'accepted', 'rejected'])
      .map((m) => this.store.toFleetDriver(m));
    const canWork = boolParam(query.get('can_work'));
    const withAlerts = boolParam(query.get('with_alerts'));
    const alerting = (d: (typeof all)[number]) => {
      const s = d.documentsSummary;
      return s.expired + s.rejected + s.expiringSoon > 0;
    };
    // Contadores con el resto de filtros, sin el de su pestaña.
    const scoped = all.filter(
      (d) =>
        (canWork === null || d.canWork === canWork) &&
        includesText([`${d.user.name} ${d.user.surname}`, d.user.email, d.user.phone, d.vehicle?.plate], query.get('search')),
    );
    const counts = countBy(scoped, (d) => d.status);
    const accepted = scoped.filter((d) => d.status === 'accepted');
    const rows = scoped
      .filter(
        (d) =>
          (!query.get('status') || d.status === query.get('status')) &&
          (!query.get('presence') || d.presence === query.get('presence')) &&
          (withAlerts === null || alerting(d) === withAlerts),
      )
      .sort((a, b) =>
        a.status === 'pending' && b.status !== 'pending'
          ? -1
          : b.status === 'pending' && a.status !== 'pending'
            ? 1
            : (b.weekStats?.fareCents ?? 0) - (a.weekStats?.fareCents ?? 0) || b.requestedAt.localeCompare(a.requestedAt),
      );
    return paginate(
      rows,
      query,
      { pending: counts['pending'] ?? 0, accepted: counts['accepted'] ?? 0, rejected: counts['rejected'] ?? 0 },
      {
        presenceCounts: {
          on_trip: accepted.filter((d) => d.presence === 'on_trip').length,
          available: accepted.filter((d) => d.presence === 'available').length,
          offline: accepted.filter((d) => d.presence === 'offline').length,
        },
        withAlerts: accepted.filter(alerting).length,
      },
    );
  }

  private showDriver(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const membership = this.membershipOr404(ctx, fleet);
    const driver = this.store.driver(membership.userId)!;
    return data({
      ...this.store.toFleetDriver(membership),
      documents: this.store.driverDocuments(driver).map((d) => this.store.documentForFleet(d)),
      shifts: (this.store.state.shifts[fleet.id]?.assignments ?? []).filter((a) => a.membershipId === membership.membershipId),
    });
  }

  private acceptDriver(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const membership = this.membershipOr404(ctx, fleet);
    if (membership.status !== 'pending') {
      conflict('membership_not_pending', 'Esta solicitud ya no está pendiente.');
    }
    const employmentType = ctx.body?.employmentType as EmploymentType | undefined;
    const share = this.validateShare(employmentType, ctx.body?.revenueSharePercentage, fleet.defaultRevenueSharePercentage, fleet.kind);
    membership.status = 'accepted';
    membership.employmentType = employmentType!;
    membership.revenueSharePercentage = share;
    membership.acceptedAt = nowIso();
    this.reassignDriverDocuments(membership.userId, fleet.id);
    return data(this.store.toFleetDriver(membership));
  }

  /** Valida el tipo de relación y el reparto (obligatorio en autónomos, prohibido en asalariados). */
  private validateShare(
    employmentType: EmploymentType | undefined,
    share: unknown,
    fallback: number,
    kind: FleetKind = 'owner_operator',
  ): number | null {
    if (kind === 'dispatch_center') {
      // Los socios de una emisora cobran íntegro: sin reparto.
      if (employmentType !== 'associate') invalid({ employment_type: ['En una emisora los taxistas entran como socios.', 'In'] });
      if (share !== undefined && share !== null) invalid({ revenue_share_percentage: ['Los socios no tienen reparto.', 'Prohibited'] });
      return null;
    }
    if (employmentType !== 'salaried' && employmentType !== 'self_employed') {
      invalid({ employment_type: ['Elige asalariado o autónomo.', 'In'] });
    }
    if (employmentType === 'salaried') {
      if (share !== undefined && share !== null) {
        invalid({ revenue_share_percentage: ['Los asalariados no tienen reparto.', 'Prohibited'] });
      }
      return null;
    }
    const value = share === undefined || share === null ? fallback : Number(share);
    if (!Number.isFinite(value) || value < 0 || value > 100) {
      invalid({ revenue_share_percentage: ['El reparto debe estar entre 0 y 100.', 'Between'] });
    }
    return value;
  }

  private reassignDriverDocuments(userId: string, fleetId: string | null): void {
    this.store.state.documents
      .filter((d) => d.ownerType === 'driver' && d.ownerId === userId)
      .forEach((d) => (d.fleetId = fleetId));
  }

  private rejectDriver(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const membership = this.membershipOr404(ctx, fleet);
    if (membership.status !== 'pending') {
      conflict('membership_not_pending', 'Esta solicitud ya no está pendiente.');
    }
    membership.status = 'rejected';
    return data(this.store.toFleetDriver(membership));
  }

  private updateDriver(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const membership = this.membershipOr404(ctx, fleet);
    const employmentType = (ctx.body?.employmentType as EmploymentType | undefined) ?? membership.employmentType ?? undefined;
    const share =
      ctx.body && 'revenueSharePercentage' in ctx.body
        ? ctx.body.revenueSharePercentage
        : employmentType === 'salaried'
          ? null
          : membership.revenueSharePercentage;
    if (ctx.body && 'active' in ctx.body) {
      membership.active = !!ctx.body.active;
      if (Object.keys(ctx.body).length === 1) return data(this.store.toFleetDriver(membership));
    }
    membership.revenueSharePercentage = this.validateShare(employmentType, share, fleet.defaultRevenueSharePercentage, fleet.kind);
    membership.employmentType = employmentType!;
    return data(this.store.toFleetDriver(membership));
  }

  private removeDriver(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const membership = this.membershipOr404(ctx, fleet);
    membership.status = 'removed';
    membership.removedAt = nowIso();
    this.reassignDriverDocuments(membership.userId, null);
    const removed = this.removeAssignments(fleet.id, (a) => a.membershipId === membership.membershipId);
    return data({ removedShiftAssignments: removed });
  }

  private removeAssignments(fleetId: string, predicate: (a: ShiftAssignment) => boolean): number {
    const shifts = this.store.state.shifts[fleetId];
    if (!shifts) return 0;
    const before = shifts.assignments.length;
    shifts.assignments = shifts.assignments.filter((a) => !predicate(a));
    const removed = before - shifts.assignments.length;
    if (removed) shifts.updatedAt = nowIso();
    return removed;
  }

  // ---------------------------------------------------------------- vehículos

  private vehicleOr404(ctx: RouteContext, fleet: MockFleet) {
    return this.store.fleetVehicles(fleet.id).find((v) => v.id === ctx.params['vehicleId']) ?? notFound();
  }

  private listVehicles(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const { query } = ctx;
    const canWork = boolParam(query.get('can_work'));
    const withoutDriver = boolParam(query.get('without_driver'));
    const withAlerts = boolParam(query.get('with_alerts'));
    const alerting = (v: FleetVehicle) =>
      v.documents.some((d) => d.status === 'rejected' || d.expiryStatus === 'expired' || d.expiryStatus === 'expiring_soon');
    const all = this.store.fleetVehicles(fleet.id).map((v) => this.store.toVehicle(v, fleet.mode));
    const scoped = all.filter(
      (v) =>
        (canWork === null || v.canWork === canWork) &&
        includesText([v.plate, normalizePlate(v.plate), v.licenseNumber, v.brand, v.model], query.get('search')),
    );
    const rows = scoped.filter(
      (v) =>
        (!query.get('status') || v.status === query.get('status')) &&
        (!query.get('presence') || v.presence === query.get('presence')) &&
        (withoutDriver === null || (v.drivers.length === 0) === withoutDriver) &&
        (withAlerts === null || alerting(v) === withAlerts),
    );
    return paginate(rows, query, countBy(scoped, (v) => v.status), {
      presenceCounts: {
        on_street: scoped.filter((v) => v.presence === 'on_street').length,
        parked: scoped.filter((v) => v.presence === 'parked').length,
      },
      withoutDriver: scoped.filter((v) => !v.drivers.length).length,
      withAlerts: scoped.filter(alerting).length,
    });
  }

  private createVehicle(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const body = ctx.body ?? {};
    const plate = normalizePlate(String(body.plate ?? ''));
    const errors: Record<string, [string, string]> = {};
    if (!/^[0-9]{4}[A-Z]{3}$|^[A-Z]{1,2}[0-9]{4}[A-Z]{1,2}$/.test(plate)) errors['plate'] = ['La matrícula no es válida.', 'Regex'];
    if (!String(body.licenseNumber ?? '').trim()) {
      errors['license_number'] = [fleet.mode === 'taxi' ? 'El nº de licencia es obligatorio.' : 'El nº de autorización es obligatorio.', 'Required'];
    }
    if (body.seats !== undefined && body.seats !== null && !(Number(body.seats) >= 1 && Number(body.seats) <= 8)) {
      errors['seats'] = ['Las plazas deben estar entre 1 y 8.', 'Between'];
    }
    const maxYear = new Date().getFullYear() + 1;
    if (body.year !== undefined && body.year !== null && !(Number(body.year) >= 1990 && Number(body.year) <= maxYear)) {
      errors['year'] = [`El año va de 1990 a ${maxYear}.`, 'Between'];
    }
    if (fleet.mode === 'taxi' && body.municipality !== undefined && !String(body.municipality ?? '').trim()) {
      errors['municipality'] = ['El municipio de la licencia es obligatorio.', 'Required'];
    }
    if (Object.keys(errors).length) invalid(errors);
    if (this.store.state.vehicles.some((v) => normalizePlate(v.plate) === plate)) {
      conflict('vehicle_plate_taken', 'Esta matrícula ya está dada de alta en una flota.');
    }
    const vehicle = {
      id: this.store.newId(),
      fleetId: fleet.id,
      ownerUserId: null,
      plate: `${plate.slice(0, 4)} ${plate.slice(4)}`.trim(),
      licenseNumber: String(body.licenseNumber).trim(),
      brand: body.brand ?? null,
      model: body.model ?? null,
      seats: body.seats ?? null,
      createdAt: nowIso(),
      year: body.year ?? null,
      odometerKm: body.odometerKm ?? null,
      municipality: fleet.mode === 'taxi' ? String(body.municipality ?? '').trim() || null : null,
      submittedAt: null,
    };
    this.store.state.vehicles.push(vehicle);
    return data(this.store.toVehicle(vehicle, fleet.mode), 201);
  }

  private showVehicle(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    return data(this.store.toVehicle(this.vehicleOr404(ctx, fleet), fleet.mode));
  }

  private updateVehicle(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const vehicle = this.vehicleOr404(ctx, fleet);
    const body = ctx.body ?? {};
    if ('plate' in body) {
      invalid({ plate: ['La matrícula no se puede cambiar.', 'Prohibited'] });
    }
    if (body.licenseNumber !== undefined) {
      if (!String(body.licenseNumber).trim()) invalid({ license_number: ['El número es obligatorio.', 'Required'] });
      vehicle.licenseNumber = String(body.licenseNumber).trim();
    }
    if (body.brand !== undefined) vehicle.brand = body.brand;
    if (body.model !== undefined) vehicle.model = body.model;
    if (body.seats !== undefined) vehicle.seats = body.seats;
    if (body.year !== undefined) vehicle.year = body.year;
    if (body.odometerKm !== undefined) vehicle.odometerKm = body.odometerKm;
    if (body.municipality !== undefined && fleet.mode === 'taxi') vehicle.municipality = body.municipality;
    return data(this.store.toVehicle(vehicle, fleet.mode));
  }

  private deleteVehicle(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const vehicle = this.vehicleOr404(ctx, fleet);
    this.store.state.vehicles = this.store.state.vehicles.filter((v) => v.id !== vehicle.id);
    const removed = this.removeAssignments(fleet.id, (a) => a.vehicleId === vehicle.id);
    return data({ removedShiftAssignments: removed });
  }

  private uploadVehicleDocument(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const vehicle = this.vehicleOr404(ctx, fleet);
    const { type, file } = this.readUpload(ctx, documentTypesFor('vehicle', fleet.mode));
    const doc = this.store.addDocumentVersion(
      'vehicle',
      vehicle.id,
      type,
      { mode: fleet.mode, fleetId: fleet.id, driverUserId: null },
      this.uploader(fleet),
      file,
    );
    return data(this.store.documentForFleet(doc), 201);
  }

  // ---------------------------------------------------------------- turnos

  private updateShiftSlots(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const slots = (Array.isArray(ctx.body) ? ctx.body : []) as ShiftSlotHours[];
    const time = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (slots.length !== 3 || !SHIFT_SLOTS.every((s) => slots.some((x) => x.slot === s))) {
      invalid({ slots: ['Hay que indicar las tres franjas.', 'Size'] });
    }
    if (slots.some((s) => !time.test(s.start) || !time.test(s.end))) {
      invalid({ slots: ['Las horas deben tener el formato HH:mm.', 'DateFormat'] });
    }
    // Minutos que cubre cada franja (la de noche puede cruzar la medianoche).
    const covered = new Set<number>();
    for (const s of slots) {
      const [sh, sm] = s.start.split(':').map(Number);
      const [eh, em] = s.end.split(':').map(Number);
      let minute = sh * 60 + sm;
      const end = eh * 60 + em;
      do {
        if (covered.has(minute)) {
          invalid({ slots: ['Las franjas no pueden solaparse.', 'NoOverlap'] });
        }
        covered.add(minute);
        minute = (minute + 1) % 1440;
      } while (minute !== end);
    }
    this.store.state.shiftSlots[fleet.id] = SHIFT_SLOTS.map((slot) => slots.find((s) => s.slot === slot)!);
    return data(this.store.state.shiftSlots[fleet.id]);
  }

  private updateShifts(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const current = this.store.state.shifts[fleet.id] ?? { assignments: [], updatedAt: null };
    const vehicleIds = new Set(this.store.fleetVehicles(fleet.id).map((v) => v.id));
    const accepted = new Set(this.store.fleetMemberships(fleet.id, ['accepted']).map((m) => m.membershipId));
    if (!vehicleIds.size || !accepted.size) {
      conflict('shifts_locked', 'Para asignar turnos hace falta al menos un vehículo y un conductor aceptado.');
    }
    if ((ctx.body?.updatedAt ?? null) !== current.updatedAt) {
      conflict('shifts_outdated', 'Alguien ha cambiado los turnos mientras los editabas. Vuelve a cargarlos.');
    }
    const assignments = ((ctx.body?.assignments ?? []) as ShiftAssignment[]).map((a) => ({
      vehicleId: a.vehicleId,
      weekday: Number(a.weekday) as ShiftAssignment['weekday'],
      slot: a.slot,
      membershipId: a.membershipId,
    }));
    const invalidRow = assignments.find(
      (a) =>
        !vehicleIds.has(a.vehicleId) ||
        !accepted.has(a.membershipId) ||
        !WEEKDAYS.includes(a.weekday) ||
        !SHIFT_SLOTS.includes(a.slot),
    );
    if (invalidRow) {
      invalid({ assignments: ['Hay asignaciones con un vehículo o un conductor que no son de la flota.', 'Exists'] });
    }
    const conflicts = findShiftConflicts(assignments);
    if (conflicts.length) {
      throw new MockApiError(422, {
        message: conflicts.length === 1 ? 'Hay 1 conflicto en los turnos' : `Hay ${conflicts.length} conflictos en los turnos`,
        error_type: 'shift_conflict',
        conflicts,
      });
    }
    this.store.state.shifts[fleet.id] = { assignments, updatedAt: nowIso() };
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return data({ ...this.store.state.shifts[fleet.id], effectiveFrom: localDate(tomorrow) });
  }

  // ---------------------------------------------------------------- documentos

  private documentsMatrix(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const columns = documentTypesFor('vehicle', fleet.mode);
    const strip = (d: MockDocument) => ({ ...this.store.documentForFleet(d), file: null });
    const rows = this.store.fleetVehicles(fleet.id).map((vehicle) => {
      const docs = this.store.vehicleDocuments(vehicle, fleet.mode);
      const urgency = mostUrgent(docs.map((d) => documentUrgency({ status: d.status, expiryStatus: expiryStatusFor(d.expiresAt) })));
      const documents: Record<string, unknown> = {};
      docs.forEach((d) => (documents[d.type] = strip(d)));
      const vehicleLabel = [vehicle.brand, vehicle.model].filter(Boolean).join(' ') || null;
      return { vehicleId: vehicle.id, plate: vehicle.plate, vehicleLabel, urgency, documents };
    });
    rows.sort((a, b) => urgencyRank(a.urgency) - urgencyRank(b.urgency) || a.plate.localeCompare(b.plate));
    const company = documentTypesFor('fleet', fleet.mode).map((type) =>
      strip(this.store.documentOrPending('fleet', fleet.id, type, { mode: fleet.mode, fleetId: fleet.id, driverUserId: null })),
    );
    const horizon = localDate(new Date(Date.now() + UPCOMING_EXPIRATIONS_DAYS * 864e5));
    const upcoming = this.allFleetDocuments(fleet)
      .filter((d) => d.status === 'approved' && d.expiresAt && d.expiresAt <= horizon)
      .map((d) => ({ ownerType: d.ownerType, ownerId: d.ownerId, ownerLabel: this.store.ownerLabel(d), type: d.type, expiresAt: d.expiresAt! }))
      .sort((a, b) => a.expiresAt.localeCompare(b.expiresAt));
    return data({ columns, company: { documents: company }, vehicles: rows, upcomingExpirations: upcoming });
  }

  private showFleetDocument(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const doc = this.store.state.documents.find((d) => d.id === ctx.params['documentId']);
    // Solo documentos de la flota y de sus vehículos: los de conductor no se ven.
    const vehicleIds = new Set(this.store.fleetVehicles(fleet.id).map((v) => v.id));
    const visible =
      doc && ((doc.ownerType === 'fleet' && doc.ownerId === fleet.id) || (doc.ownerType === 'vehicle' && vehicleIds.has(doc.ownerId)));
    if (!visible) notFound();
    return data(this.store.documentForFleet(doc!));
  }

  // ---------------------------------------------------------------- liquidaciones

  private settlementsSummary(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const movements = this.store.state.movements[fleet.id] ?? [];
    // El mismo saldo y la misma fecha que el extracto de la semana en curso.
    const week = this.buildStatement(fleet, 'week', new Date());
    const lastTwoDays = movements.filter((m) => Date.now() - new Date(m.occurredAt).getTime() < 2 * 86_400_000);
    return data({
      balanceCents: Math.max(week.closingBalanceCents, 0),
      pendingCents: lastTwoDays.reduce((sum, m) => sum + (m.type === 'payout' ? 0 : m.amountCents), 0),
      nextPayoutAt: movements.length && week.availableOn ? `${week.availableOn}T07:00:00Z` : null,
      defaultRevenueSharePercentage: fleet.defaultRevenueSharePercentage,
      payoutsEnabled: false,
    });
  }

  private settlementMovements(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const { query } = ctx;
    const from = query.get('from');
    const to = query.get('to');
    const rows = (this.store.state.movements[fleet.id] ?? []).filter(
      (m) =>
        (!query.get('type') || m.type === query.get('type')) &&
        (!query.get('membership_id') || m.driver?.membershipId === query.get('membership_id')) &&
        (!from || dateOnly(m.occurredAt) >= from) &&
        (!to || dateOnly(m.occurredAt) <= to),
    );
    return paginate(rows, query);
  }

  private updateSettlementSettings(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const value = Number(ctx.body?.defaultRevenueSharePercentage);
    if (!Number.isFinite(value) || value < 30 || value > 80) {
      invalid({ default_revenue_share_percentage: ['El reparto debe estar entre 30 y 80.', 'Between'] });
    }
    fleet.defaultRevenueSharePercentage = value;
    return this.settlementsSummary(ctx);
  }

  // ---------------------------------------------------------------- rendimiento e informes

  private performanceRows(fleet: MockFleet, from: string | null, to: string | null) {
    const trips = (this.store.state.movements[fleet.id] ?? []).filter(
      (m) => m.type === 'trip_earning' && (!from || dateOnly(m.occurredAt) >= from) && (!to || dateOnly(m.occurredAt) <= to),
    );
    return this.store.fleetMemberships(fleet.id, ['accepted']).map((membership) => {
      const driver = this.store.driver(membership.userId)!;
      const own = trips.filter((m) => m.driver?.membershipId === membership.membershipId);
      const unit = hashUnit(membership.membershipId);
      return {
        membershipId: membership.membershipId,
        name: `${driver.user.name} ${driver.user.surname}`,
        trips: own.length,
        hoursOnline: Math.round(own.length * (1.4 + unit) * 10) / 10,
        fareTotalCents: own.reduce((sum, m) => sum + m.amountCents, 0),
        acceptanceRate: Math.round((0.82 + unit * 0.17) * 100) / 100,
        cancellationRate: Math.round((0.01 + (1 - unit) * 0.08) * 1000) / 1000,
        rating: own.length ? Math.round((4.3 + unit * 0.7) * 10) / 10 : null,
      };
    }).map((row) => ({ ...row, fareCentsPerHour: row.hoursOnline ? Math.round(row.fareTotalCents / row.hoursOnline) : 0 }));
  }

  private driversPerformance(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const { query } = ctx;
    const sortKey = ({
      trips: 'trips',
      fare_total: 'fareTotalCents',
      hours_online: 'hoursOnline',
      acceptance_rate: 'acceptanceRate',
      fare_per_hour: 'fareCentsPerHour',
      rating: 'rating',
    } as const)[(query.get('sort') ?? 'fare_per_hour') as 'trips'] ?? 'fareCentsPerHour';
    const direction = query.get('order') === 'asc' ? 1 : -1;
    const rows = this.performanceRows(fleet, query.get('from'), query.get('to')).sort(
      (a, b) => direction * ((a[sortKey] as number) - (b[sortKey] as number)),
    );
    return paginate(rows, query);
  }

  private exportReport(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const report = ctx.params['report'];
    const format = ctx.query.get('format') === 'pdf' ? 'pdf' : 'csv';
    if (!['trips', 'drivers', 'settlements', 'driver_settlements', 'route_sheets'].includes(report)) notFound();
    if (report === 'route_sheets' && fleet.mode !== 'vtc') notFound();
    const month = ctx.query.get('month');
    const from = month ? `${month}-01` : ctx.query.get('from');
    const to = month ? localDate(new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0)) : ctx.query.get('to');
    if (report === 'driver_settlements' || report === 'route_sheets') {
      return this.exportMonthly(fleet, report, from, to, month ?? `${from}_${to}`);
    }
    let lines: string[];
    if (report === 'drivers') {
      lines = ['conductor;viajes;horas;importe_eur;aceptacion', ...this.performanceRows(fleet, from, to).map((r) => `${r.name};${r.trips};${r.hoursOnline};${(r.fareTotalCents / 100).toFixed(2)};${r.acceptanceRate}`)];
    } else {
      const movements = (this.store.state.movements[fleet.id] ?? []).filter(
        (m) => (report === 'settlements' || m.type === 'trip_earning') && (!from || dateOnly(m.occurredAt) >= from) && (!to || dateOnly(m.occurredAt) <= to),
      );
      lines = [
        'fecha;tipo;conductor;canal;servicio;precio_viaje_eur;cuota_pasajero_eur;total_pasajero_eur;importe_flota_eur',
        ...movements.map((m) =>
          [
            m.occurredAt,
            m.type,
            m.driver?.name ?? '',
            m.trip?.demandChannel ?? '',
            m.trip?.serviceType ?? '',
            m.trip ? (m.trip.fareCents / 100).toFixed(2) : '',
            m.trip ? (m.trip.feeCents / 100).toFixed(2) : '',
            m.trip ? (m.trip.passengerTotalCents / 100).toFixed(2) : '',
            (m.amountCents / 100).toFixed(2),
          ].join(';'),
        ),
      ];
    }
    const filename = `${report}-${fleet.fleetCode}.${format}`;
    const blob = format === 'csv' ? new Blob(['﻿' + lines.join('\n')], { type: 'text/csv' }) : simplePdf(`Informe ${report} · ${fleet.name}`, lines.slice(0, 40));
    return { status: 200, body: null, blob, headers: { 'Content-Disposition': `attachment; filename="${filename}"` } };
  }

  // ---------------------------------------------------------------- equipo y ajustes

  private listMembers(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    return data(this.store.state.members.filter((m) => m.fleetId === fleet.id).map(({ fleetId, ...member }) => member));
  }

  private inviteMember(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const email = String(ctx.body?.email ?? '').trim().toLowerCase();
    const role = ctx.body?.role as FleetRole;
    const errors: Record<string, [string, string]> = {};
    if (!/^\S+@\S+\.\S+$/.test(email)) errors['email'] = ['El correo no es válido.', 'Email'];
    if (!FLEET_ROLES.includes(role)) errors['role'] = ['El rol no es válido.', 'In'];
    if (Object.keys(errors).length) invalid(errors);
    if (this.store.state.members.some((m) => m.fleetId === fleet.id && m.email.toLowerCase() === email)) {
      conflict('member_already_exists', 'Esta persona ya está en el equipo.');
    }
    const name = String(ctx.body?.name ?? '').trim() || null;
    const member = { id: this.store.newId(), fleetId: fleet.id, email, name, user: null, role, status: 'invited' as const, invitedAt: nowIso(), lastSeenAt: null };
    this.store.state.members.push(member);
    const { fleetId, ...rest } = member;
    return data(rest, 201);
  }

  private memberOr404(ctx: RouteContext, fleet: MockFleet) {
    return this.store.state.members.find((m) => m.fleetId === fleet.id && m.id === ctx.params['memberId']) ?? notFound();
  }

  private assertNotLastOwner(fleet: MockFleet, memberId: string): void {
    const owners = this.store.state.members.filter((m) => m.fleetId === fleet.id && m.role === 'fleet_owner' && m.status === 'active');
    if (owners.length === 1 && owners[0].id === memberId) {
      conflict('last_owner', 'La flota tiene que tener al menos un propietario.');
    }
  }

  private updateMember(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const member = this.memberOr404(ctx, fleet);
    const role = ctx.body?.role as FleetRole;
    if (!FLEET_ROLES.includes(role)) invalid({ role: ['El rol no es válido.', 'In'] });
    if (member.role === 'fleet_owner' && role !== 'fleet_owner') this.assertNotLastOwner(fleet, member.id);
    member.role = role;
    const { fleetId, ...rest } = member;
    return data(rest);
  }

  private removeMember(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const member = this.memberOr404(ctx, fleet);
    if (member.role === 'fleet_owner') this.assertNotLastOwner(fleet, member.id);
    this.store.state.members = this.store.state.members.filter((m) => m.id !== member.id);
    return ok(null, 204);
  }

  private updateNotifications(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const b = ctx.body ?? {};
    this.store.state.notifications[fleet.id] = {
      documentExpiring: !!b.documentExpiring,
      documentRejected: !!b.documentRejected,
      driverRequests: !!b.driverRequests,
      weeklySettlement: !!b.weeklySettlement,
    };
    return data(this.store.state.notifications[fleet.id]);
  }

  // ---------------------------------------------------------------- admin: verificación

  /** Documentos que pasan por la cola: los vigentes subidos, menos el DNI (Tecalis). */
  private reviewableDocuments(): MockDocument[] {
    return this.store.state.documents.filter((d) => {
      if (d.superseded || d.status === 'pending' || d.type === 'identity') return false;
      // Lo de una flota sin enviar no se revisa todavía.
      const fleet = d.fleetId ? this.store.fleet(d.fleetId) : undefined;
      if (fleet && !fleet.submittedAt && d.ownerType !== 'driver') return false;
      // Ni los de un vehículo que aún no se ha enviado a revisión.
      const vehicle = d.ownerType === 'vehicle' ? this.store.state.vehicles.find((v) => v.id === d.ownerId) : undefined;
      return !(vehicle && vehicle.fleetId && !vehicle.submittedAt);
    });
  }

  private reviewableOr404(ctx: RouteContext): MockDocument {
    return this.store.state.documents.find((d) => d.id === ctx.params['documentId'] && d.status !== 'pending') ?? notFound();
  }

  private listReviews(ctx: RouteContext): MockResponse {
    const { query } = ctx;
    const status = (query.get('status') ?? 'in_review') as DocumentStatus | '';
    const all = this.reviewableDocuments();
    const scoped = all.filter(
      (d) =>
        (!query.get('owner_type') || d.ownerType === query.get('owner_type')) &&
        (!query.get('type') || d.type === query.get('type')) &&
        (!query.get('mode') || d.mode === query.get('mode')),
    );
    const counts = countBy(scoped, (d) => d.status);
    const items = scoped
      .filter((d) => !status || d.status === status)
      .map((d) => this.store.reviewItem(d))
      .filter((item) => includesText([item.owner.label, item.fleet?.name, item.driver?.name], query.get('search')));
    const newestFirst = query.get('sort') === '-uploaded_at';
    items.sort((a, b) => {
      const diff = (a.document.uploadedAt ?? '').localeCompare(b.document.uploadedAt ?? '');
      return newestFirst ? -diff : diff;
    });
    return paginate(items, query, { in_review: counts['in_review'] ?? 0, approved: counts['approved'] ?? 0, rejected: counts['rejected'] ?? 0 });
  }

  private assertReviewable(doc: MockDocument): void {
    if (doc.type === 'identity') {
      invalid({ document: ['El DNI lo verifica Tecalis: no se revisa a mano.', 'Prohibited'] });
    }
    if (doc.status !== 'in_review' || doc.superseded) {
      conflict('document_already_reviewed', 'Este documento ya se ha revisado.');
    }
  }

  private approveDocument(ctx: RouteContext): MockResponse {
    const doc = this.reviewableOr404(ctx);
    this.assertReviewable(doc);
    const info = documentTypeInfo(doc.type);
    const expiresAt = ctx.body?.expiresAt ?? null;
    const licenseNumber = ctx.body?.licenseNumber ?? null;
    const errors: Record<string, [string, string]> = {};
    if (info?.hasExpiry && !/^\d{4}-\d{2}-\d{2}$/.test(String(expiresAt ?? ''))) {
      errors['expires_at'] = ['La fecha de caducidad es obligatoria.', 'Required'];
    }
    if (info?.requiresLicenseNumber && !String(licenseNumber ?? '').trim()) {
      errors['license_number'] = ['El nº de licencia o autorización es obligatorio.', 'Required'];
    }
    if (Object.keys(errors).length) invalid(errors);
    const at = nowIso();
    Object.assign(doc, {
      status: 'approved',
      expiresAt: info?.hasExpiry ? expiresAt : null,
      licenseNumber: info?.requiresLicenseNumber ? String(licenseNumber).trim() : null,
      rejectionReason: null,
      rejectionComment: null,
      reviewedAt: at,
      reviewedBy: MOCK_REVIEWER,
    });
    doc.history.push({ action: 'approved', actor: MOCK_REVIEWER, at, reason: null, comment: null });
    return data(this.store.documentForAdmin(doc, true));
  }

  private rejectDocument(ctx: RouteContext): MockResponse {
    const doc = this.reviewableOr404(ctx);
    this.assertReviewable(doc);
    const reason = ctx.body?.reason as RejectionReason;
    const comment = String(ctx.body?.comment ?? '').trim() || null;
    if (!REJECTION_REASONS.includes(reason)) invalid({ reason: ['Elige un motivo de rechazo.', 'In'] });
    if (reason === 'other' && !comment) invalid({ comment: ['Explica el motivo.', 'RequiredIf'] });
    const at = nowIso();
    Object.assign(doc, { status: 'rejected', rejectionReason: reason, rejectionComment: comment, reviewedAt: at, reviewedBy: MOCK_REVIEWER });
    doc.history.push({ action: 'rejected', actor: MOCK_REVIEWER, at, reason, comment });
    return data(this.store.documentForAdmin(doc, true));
  }

  // ---------------------------------------------------------------- admin: conductores profesionales

  private listProfessionalDrivers(ctx: RouteContext): MockResponse {
    const { query } = ctx;
    const scoped = this.store.state.drivers
      .filter((d) => this.store.state.memberships.some((m) => m.userId === d.user.id && m.status === 'accepted') || d.ownLicense)
      .map((d) => this.store.toProfessionalDriver(d))
      .filter(
        (d) =>
          (!query.get('mode') || d.mode === query.get('mode')) &&
          (!query.get('working_as') || d.workingAs === query.get('working_as')) &&
          includesText([`${d.user.name} ${d.user.surname}`, d.user.email, d.user.phone, d.fleet?.name], query.get('search')),
      );
    const status = query.get('onboarding_status') as ProfessionalDriverOnboardingStatus | null;
    const rows = scoped
      .filter((d) => !status || d.onboardingStatus === status)
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
    return paginate(rows, query, countBy(scoped, (d) => d.onboardingStatus));
  }

  private showProfessionalDriver(ctx: RouteContext): MockResponse {
    const driver = this.store.driver(ctx.params['userId']) ?? notFound();
    const vehicle = driver.ownLicense ? this.store.ownVehicle(driver.user.id) : undefined;
    const docs = [...this.store.driverDocuments(driver), ...(vehicle ? this.store.vehicleDocuments(vehicle, driver.mode) : [])];
    const fleets = this.store.state.memberships
      .filter((m) => m.userId === driver.user.id && m.status !== 'pending' && m.status !== 'rejected')
      .map((m) => ({
        id: m.fleetId,
        name: this.store.fleet(m.fleetId)?.name ?? '—',
        status: m.status as FleetMembershipStatus,
        since: m.acceptedAt ?? m.requestedAt,
        until: m.removedAt,
      }));
    return data({
      ...this.store.toProfessionalDriver(driver),
      documents: docs.map((d) => this.store.documentForAdmin(d, false)),
      billing: driver.billing,
      fleets,
    });
  }

  // ---------------------------------------------------------------- mockup 28

  private overviewWeek(fleet: MockFleet) {
    const st = this.buildStatement(fleet, 'week', new Date());
    return {
      from: st.from,
      to: st.to,
      trips: st.lines.trips.count,
      fareCents: st.lines.trips.amountCents,
      driverShareCents: -st.lines.driverShare.amountCents || 0,
      netCents: st.lines.trips.amountCents + st.lines.driverShare.amountCents,
      feeCents: st.feeCents,
      payoutOn: st.availableOn,
    };
  }

  private emptyWeek() {
    const { from, to } = this.periodOf('week', new Date());
    return { from, to, trips: 0, fareCents: 0, driverShareCents: 0, netCents: 0, feeCents: 0, payoutOn: null };
  }

  /** «Necesita tu atención»: lo más urgente primero. */
  private attention(fleet: MockFleet, docs: MockDocument[], withoutVehicle: MockMembership[]) {
    const label = (d: MockDocument) => {
      const owner =
        d.ownerType === 'vehicle'
          ? this.store.state.vehicles.find((v) => v.id === d.ownerId)?.plate
          : d.ownerType === 'driver'
            ? (() => {
                const u = this.store.driver(d.ownerId)?.user;
                return u ? `${u.name} ${u.surname}` : '';
              })()
            : fleet.name;
      return { type: d.ownerType, owner: owner ?? '' };
    };
    const driverDocs = this.store
      .fleetMemberships(fleet.id, ['accepted'])
      .flatMap((m) => this.store.driverDocuments(this.store.driver(m.userId)!));
    const all = [...docs, ...driverDocs];
    const items: { kind: string; label: string; detail: string | null; target: { type: string; id: string }; rank: number }[] = [];
    for (const d of all) {
      const expired = d.status === 'approved' && expiryStatusFor(d.expiresAt) === 'expired';
      if (!expired && d.status !== 'rejected') continue;
      const { type, owner } = label(d);
      items.push({
        kind: expired ? 'document_expired' : 'document_rejected',
        label: `${DOC_NAME[d.type] ?? d.type} · ${owner}`,
        detail: expired ? d.expiresAt : d.rejectionReason,
        target: { type: type === 'fleet' ? 'fleet' : type, id: d.ownerId },
        rank: expired ? 0 : 1,
      });
    }
    for (const m of withoutVehicle) {
      const u = this.store.driver(m.userId)!.user;
      items.push({ kind: 'driver_without_vehicle', label: `${u.name} ${u.surname}`, detail: null, target: { type: 'driver', id: m.membershipId }, rank: 2 });
    }
    for (const m of this.store.fleetMemberships(fleet.id, ['pending'])) {
      const u = this.store.driver(m.userId)!.user;
      items.push({ kind: 'driver_request', label: `${u.name} ${u.surname}`, detail: m.requestedAt, target: { type: 'driver', id: m.membershipId }, rank: 3 });
    }
    return items.sort((a, b) => a.rank - b.rank).map(({ rank, ...item }) => item);
  }

  private submitVehicle(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const vehicle = this.vehicleOr404(ctx, fleet);
    if (vehicle.submittedAt) conflict('vehicle_already_submitted', 'Este vehículo ya se envió a revisión.');
    const docs = this.store.vehicleDocuments(vehicle, fleet.mode);
    const missing = docs.filter((d) => d.status === 'pending').map((d) => d.type);
    if (missing.length) {
      conflict('vehicle_documents_missing', 'Faltan documentos por subir.', { missing });
    }
    vehicle.submittedAt = nowIso();
    return data(this.store.toVehicle(vehicle, fleet.mode));
  }

  private regionCenter(fleet: MockFleet): [number, number] {
    const centers: Record<string, [number, number]> = {
      'r-manresa': [41.7287, 1.8236], 'r-barcelona': [41.3874, 2.1686], 'r-sabadell': [41.5463, 2.1086],
      'r-igualada': [41.5786, 1.6172], 'r-zaragoza': [41.6488, -0.8891],
    };
    return centers[fleet.regionId] ?? centers['r-barcelona'];
  }

  private presence(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const [lat, lng] = this.regionCenter(fleet);
    const bucket = Math.floor(Date.now() / 30_000);
    const drivers = this.store.fleetMemberships(fleet.id, ['accepted']).map((m) => {
      const presence = this.store.driverPresence(m);
      const user = this.store.driver(m.userId)!.user;
      const jitter = (salt: string) => (hashUnit(m.membershipId + salt) - 0.5) * 0.04;
      // Se mueven un poco cada 30 s, como si circularan.
      const drift = (salt: string) => (hashUnit(m.membershipId + salt + bucket) - 0.5) * 0.003;
      return {
        membershipId: m.membershipId,
        name: `${user.name} ${user.surname}`,
        presence,
        plate: this.store.driverVehicle(m)?.plate ?? null,
        location: presence === 'offline' ? null : { lat: +(lat + jitter('a') + drift('c')).toFixed(5), lng: +(lng + jitter('b') + drift('d')).toFixed(5) },
        updatedAt: nowIso(),
      };
    });
    return data({
      counts: {
        on_trip: drivers.filter((d) => d.presence === 'on_trip').length,
        available: drivers.filter((d) => d.presence === 'available').length,
        offline: drivers.filter((d) => d.presence === 'offline').length,
      },
      drivers,
      center: { lat, lng },
      updatedAt: nowIso(),
    });
  }

  /** Movimientos de la flota entre dos fechas (`YYYY-MM-DD`, ambas incluidas). */
  private movementsBetween(fleet: MockFleet, from: string, to: string) {
    return (this.store.state.movements[fleet.id] ?? []).filter((m) => dateOnly(m.occurredAt) >= from && dateOnly(m.occurredAt) <= to);
  }

  private periodOf(period: 'week' | 'month', date: Date): { from: string; to: string } {
    if (period === 'month') {
      return { from: localDate(new Date(date.getFullYear(), date.getMonth(), 1)), to: localDate(new Date(date.getFullYear(), date.getMonth() + 1, 0)) };
    }
    const monday = FleetMockStore.weekStart(date);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: localDate(monday), to: localDate(sunday) };
  }

  /** Extracto de un periodo: la base de Liquidaciones, de la portada («Esta semana») y de Informes. */
  private buildStatement(fleet: MockFleet, period: 'week' | 'month', date: Date) {
    const { from, to } = this.periodOf(period, date);
    const all = this.store.state.movements[fleet.id] ?? [];
    const inRange = this.movementsBetween(fleet, from, to);
    const sum = (items: typeof inRange) => items.reduce((total, m) => total + m.amountCents, 0);
    const trips = inRange.filter((m) => m.type === 'trip_earning');
    const shares = inRange.filter((m) => m.type === 'driver_share');
    const adjustments = inRange.filter((m) => m.type === 'adjustment');
    // Peajes y cancelaciones cobradas: el mock no los guarda; salen de cada viaje
    // (peaje en los de aeropuerto, cancelación cobrada en ~1 de cada 75) para
    // que el saldo inicial de un periodo sea el final del anterior.
    const tollsOf = (items: typeof inRange) => items.filter((m) => m.trip?.serviceType === 'airport_premium').length * TOLL_CENTS;
    const cancelledOf = (items: typeof inRange) => items.filter((m) => m.amountCents % 75 === 0).length;
    const before = all.filter((m) => dateOnly(m.occurredAt) < from);
    const tripsBefore = before.filter((m) => m.type === 'trip_earning');
    const tolls = tollsOf(trips);
    const cancellationsCount = cancelledOf(trips);
    const cancellations = cancellationsCount * CANCELLATION_FEE_CENTS;
    const opening = sum(before) + tollsOf(tripsBefore) + cancelledOf(tripsBefore) * CANCELLATION_FEE_CENTS;
    const payouts = sum(inRange.filter((m) => m.type === 'payout'));
    const closing = opening + sum(trips) + tolls + sum(shares) + cancellations + sum(adjustments) + payouts;
    const today = localDate(new Date());
    const closed = to < today;
    const available = new Date(`${to}T00:00:00`);
    // Se liquida el lunes siguiente al domingo que cierra la semana.
    available.setDate(available.getDate() + 1);
    const byDriver = new Map<string, { membershipId: string; name: string; generatedCents: number; payableCents: number }>();
    for (const m of trips) {
      if (!m.driver) continue;
      const row = byDriver.get(m.driver.membershipId) ?? { ...m.driver, generatedCents: 0, payableCents: 0 };
      row.generatedCents += m.amountCents;
      byDriver.set(m.driver.membershipId, row);
    }
    for (const m of shares) {
      if (m.driver && byDriver.has(m.driver.membershipId)) byDriver.get(m.driver.membershipId)!.payableCents += -m.amountCents;
    }
    const drivers = [...byDriver.values()]
      .map((row) => {
        const membership = this.store.state.memberships.find((x) => x.membershipId === row.membershipId);
        return {
          ...row,
          sharePercentage: membership?.revenueSharePercentage ?? null,
          status: (closed ? 'paid' : 'pending') as 'paid' | 'pending',
          paidAt: closed ? `${localDate(available)}T08:12:00Z` : null,
        };
      })
      // Los asalariados no tienen reparto: no salen.
      .filter((row) => row.sharePercentage !== null)
      .sort((a, b) => b.generatedCents - a.generatedCents);
    return {
      period,
      from,
      to,
      openingBalanceCents: opening,
      lines: {
        trips: { count: trips.length, amountCents: sum(trips) },
        tolls: { amountCents: tolls },
        driverShare: { amountCents: sum(shares) },
        cancellations: { count: cancellationsCount, amountCents: cancellations },
        adjustments: { amountCents: sum(adjustments) },
        payouts: { amountCents: payouts },
      },
      feeCents: trips.reduce((total, m) => total + (m.trip?.feeCents ?? 0), 0),
      closingBalanceCents: closing,
      availableOn: localDate(available),
      heldCents: closed ? 0 : sum(inRange.filter((m) => Date.now() - Date.parse(m.occurredAt) < 2 * 86_400_000)),
      drivers,
    };
  }

  private statement(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const period = ctx.query.get('period') === 'month' ? 'month' : 'week';
    const raw = ctx.query.get('date');
    const date = raw && /^\d{4}-\d{2}-\d{2}$/.test(raw) ? new Date(`${raw}T12:00:00`) : new Date();
    return data(this.buildStatement(fleet, period, date));
  }

  private performanceSummary(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const from = ctx.query.get('from') ?? localDate(new Date(Date.now() - 27 * 86_400_000));
    const rows = this.performanceRows(fleet, from, ctx.query.get('to')).filter((r) => r.trips > 0);
    if (!rows.length) return data({ ratingAvg: null, acceptanceRate: null, cancellationRate: null, fareCentsPerHour: null });
    const avg = (values: number[]) => values.reduce((a, b) => a + b, 0) / values.length;
    const hours = rows.reduce((total, r) => total + r.hoursOnline, 0);
    return data({
      ratingAvg: Math.round(avg(rows.map((r) => r.rating ?? 0)) * 10) / 10,
      acceptanceRate: Math.round(avg(rows.map((r) => r.acceptanceRate)) * 100) / 100,
      cancellationRate: Math.round(avg(rows.map((r) => r.cancellationRate)) * 1000) / 1000,
      fareCentsPerHour: hours ? Math.round(rows.reduce((total, r) => total + r.fareTotalCents, 0) / hours) : null,
    });
  }

  private demandByHour(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const since = Date.now() - 28 * 86_400_000;
    const trips = (this.store.state.movements[fleet.id] ?? []).filter((m) => m.type === 'trip_earning' && Date.parse(m.occurredAt) >= since);
    const vehicles = this.store.fleetVehicles(fleet.id).length;
    const slots = this.store.state.shiftSlots[fleet.id] ?? [];
    const assignments = this.store.state.shifts[fleet.id]?.assignments ?? [];
    const slotAt = (hour: number) =>
      slots.find((s) => {
        const start = +s.start.slice(0, 2);
        const end = +s.end.slice(0, 2);
        return start < end ? hour >= start && hour < end : hour >= start || hour < end;
      })?.slot;
    return data({
      weeks: 4,
      hours: Array.from({ length: 24 }, (_, hour) => {
        const slot = slotAt(hour);
        // Coches con conductor a esa hora, de media en la semana.
        const covered = slot ? new Set(assignments.filter((a) => a.slot === slot).map((a) => `${a.vehicleId}|${a.weekday}`)).size / 7 : 0;
        return {
          hour,
          trips: Math.round(trips.filter((m) => new Date(m.occurredAt).getHours() === hour).length / 4),
          vehiclesParked: Math.max(0, Math.round(vehicles - covered)),
        };
      }),
    });
  }

  private reportMonths(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const months = [...new Set((this.store.state.movements[fleet.id] ?? []).map((m) => m.occurredAt.slice(0, 7)))].sort().reverse();
    const current = localDate(new Date()).slice(0, 7);
    return data(
      months.map((month) => {
        const st = this.buildStatement(fleet, 'month', new Date(`${month}-15T12:00:00`));
        const closed = month < current;
        return {
          month,
          closed,
          closedAt: closed ? `${st.to}T22:00:00Z` : null,
          trips: st.lines.trips.count,
          fareCents: st.lines.trips.amountCents,
          driverShareCents: -st.lines.driverShare.amountCents || 0,
          netCents: st.lines.trips.amountCents + st.lines.driverShare.amountCents,
          feeCents: st.feeCents,
          driversPaid: st.drivers.length,
        };
      }),
    );
  }

  private exportMonthly(fleet: MockFleet, report: string, from: string | null, to: string | null, label: string): MockResponse {
    const st = this.buildStatement(fleet, 'month', new Date(`${from ?? localDate(new Date())}T12:00:00`));
    if (report === 'route_sheets') {
      const lines = ['matricula;servicios;comunicadas', ...this.store.fleetVehicles(fleet.id).map((v) => {
        const n = this.movementsBetween(fleet, st.from, st.to).filter((m) => m.type === 'trip_earning').length;
        const own = Math.round(n / Math.max(1, this.store.fleetVehicles(fleet.id).length));
        return `${v.plate};${own};${own}`;
      })];
      return { status: 200, body: null, blob: new Blob(['﻿' + lines.join('\n')], { type: 'text/csv' }), headers: { 'Content-Disposition': `attachment; filename="hojas-de-ruta-${label}.csv"` } };
    }
    const lines = st.drivers.map((d) => `${d.name}: generado ${(d.generatedCents / 100).toFixed(2)} €, reparto ${d.sharePercentage} %, a pagar ${(d.payableCents / 100).toFixed(2)} €`);
    return {
      status: 200,
      body: null,
      blob: simplePdf(`Liquidación por conductor · ${fleet.name} · ${label}`, lines.length ? lines : ['Sin viajes en el periodo.']),
      headers: { 'Content-Disposition': `attachment; filename="liquidaciones-${label}.pdf"` },
    };
  }

  private updateAccounting(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const enabled = !!ctx.body?.monthlyEmailEnabled;
    const email = String(ctx.body?.email ?? '').trim() || null;
    if (email && !/^\S+@\S+\.\S+$/.test(email)) invalid({ email: ['El correo no es válido.', 'Email'] });
    if (enabled && !email) invalid({ email: ['Pon el correo de la gestoría.', 'RequiredIf'] });
    fleet.accounting = { monthlyEmailEnabled: enabled, email };
    return data({ ...fleet.accounting });
  }

  private resendMember(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const member = this.store.state.members.find((m) => m.fleetId === fleet.id && m.id === ctx.params['memberId']);
    if (!member) notFound();
    if (member.status !== 'invited') conflict('member_already_active', 'Esta persona ya ha entrado al panel.');
    member.invitedAt = nowIso();
    const { fleetId, ...rest } = member;
    return data(rest);
  }

  private updateRules(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const b = ctx.body ?? {};
    fleet.rules = {
      expiryReminders: !!b.expiryReminders,
      blockOnExpiry: !!b.blockOnExpiry,
      acceptCash: !!b.acceptCash,
      cancellationAlert: !!b.cancellationAlert,
    };
    return data(this.store.toFleet(fleet));
  }

  private updateBankAccount(ctx: RouteContext): MockResponse {
    const fleet = this.fleetOr404(ctx);
    const form = ctx.body instanceof FormData ? ctx.body : null;
    const iban = String(form?.get('iban') ?? ctx.body?.iban ?? '').replace(/\s/g, '').toUpperCase();
    if (!/^ES\d{22}$/.test(iban)) invalid({ iban: ['El IBAN no es válido (ES y 22 cifras).', 'Iban'] });
    const file = (form?.get('file') as File | null) ?? null;
    if (!file) invalid({ file: ['Sube el certificado de titularidad del IBAN nuevo.', 'Required'] });
    this.store.addDocumentVersion(
      'fleet',
      fleet.id,
      'bank_ownership_certificate',
      { mode: fleet.mode, fleetId: fleet.id, driverUserId: null },
      this.uploader(fleet),
      file,
    );
    fleet.ibanMasked = `ES${iban.slice(2, 4)} •••• ${iban.slice(-4)}`;
    return data(this.store.toFleet(fleet));
  }

  // ---------------------------------------------------------------- emisora (fase 4a)

  private dispatchCenterOr409(ctx: RouteContext): MockFleet {
    const fleet = this.fleetOr404(ctx);
    if (fleet.kind !== 'dispatch_center') {
      conflict('not_a_dispatch_center', 'Esta flota no es una emisora.');
    }
    return fleet;
  }

  private validatePolygon(value: unknown, field: string): GeoPoint[] {
    const points = Array.isArray(value) ? (value as unknown[]) : [];
    const polygon = points
      .filter((p): p is [number, number] => Array.isArray(p) && p.length === 2 && p.every((n) => Number.isFinite(Number(n))))
      .map(([lat, lng]) => [Number(lat), Number(lng)] as GeoPoint);
    if (polygon.length !== points.length || !isValidPolygon(polygon)) {
      invalid({ [field]: ['La zona tiene que tener al menos 3 puntos y sus lados no pueden cruzarse.', 'Polygon'] });
    }
    return polygon;
  }

  private toStand(stand: MockState['stands'][number]) {
    const { fleetId, ...rest } = stand;
    return { ...rest, queueCount: (this.store.state.standQueues[stand.id] ?? []).length };
  }

  private standOr404(ctx: RouteContext, fleet: MockFleet) {
    const stand = this.store.state.stands.find((s) => s.id === ctx.params['standId'] && s.fleetId === fleet.id);
    if (!stand) notFound();
    return stand;
  }

  private listStands(ctx: RouteContext): MockResponse {
    const fleet = this.dispatchCenterOr409(ctx);
    return data(
      this.store.state.stands
        .filter((s) => s.fleetId === fleet.id)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((s) => this.toStand(s)),
    );
  }

  private saveStand(ctx: RouteContext): MockResponse {
    const fleet = this.dispatchCenterOr409(ctx);
    const existing = ctx.params['standId'] ? this.standOr404(ctx, fleet) : null;
    const body = { ...(existing ?? {}), ...(ctx.body ?? {}) };
    const name = String(body.name ?? '').trim();
    if (!name) invalid({ name: ['El nombre es obligatorio.', 'Required'] });
    const geofence = this.validatePolygon(body.geofence, 'geofence');
    const lat = Number(body.location?.lat);
    const lng = Number(body.location?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || !pointInPolygon([lat, lng], geofence)) {
      invalid({ location: ['El punto de la parada tiene que estar dentro de su geocerca.', 'Within'] });
    }
    const capacity = body.capacity === null || body.capacity === undefined || body.capacity === '' ? null : Number(body.capacity);
    if (capacity !== null && !(capacity >= 1 && capacity <= 50)) invalid({ capacity: ['La capacidad va de 1 a 50.', 'Between'] });
    const stand = {
      id: existing?.id ?? this.store.newId(),
      fleetId: fleet.id,
      name,
      address: body.address ? String(body.address).trim() : null,
      location: { lat, lng },
      geofence,
      capacity,
      active: body.active !== false,
      updatedAt: nowIso(),
    };
    if (existing) {
      Object.assign(existing, stand);
    } else {
      this.store.state.stands.push(stand);
      this.store.state.standQueues[stand.id] = [];
    }
    return data(this.toStand(existing ?? stand), existing ? 200 : 201);
  }

  private deleteStand(ctx: RouteContext): MockResponse {
    const fleet = this.dispatchCenterOr409(ctx);
    const stand = this.standOr404(ctx, fleet);
    const removed = (this.store.state.standQueues[stand.id] ?? []).length;
    this.store.state.stands = this.store.state.stands.filter((s) => s.id !== stand.id);
    delete this.store.state.standQueues[stand.id];
    return data({ removedFromQueue: removed });
  }

  private standQueue(ctx: RouteContext): MockResponse {
    const fleet = this.dispatchCenterOr409(ctx);
    const stand = this.standOr404(ctx, fleet);
    const entries = this.store.state.standQueues[stand.id] ?? [];
    return data({ entries, updatedAt: entries.length ? entries[entries.length - 1].joinedAt : stand.updatedAt });
  }

  private removeFromQueue(ctx: RouteContext): MockResponse {
    const fleet = this.dispatchCenterOr409(ctx);
    const stand = this.standOr404(ctx, fleet);
    if (!String(ctx.body?.reason ?? '').trim()) invalid({ reason: ['Indica el motivo.', 'Required'] });
    const queue = this.store.state.standQueues[stand.id] ?? [];
    if (!queue.some((e) => e.membershipId === ctx.params['membershipId'])) notFound();
    this.store.state.standQueues[stand.id] = queue
      .filter((e) => e.membershipId !== ctx.params['membershipId'])
      .map((e, index) => ({ ...e, position: index + 1 }));
    return this.standQueue(ctx);
  }

  private zoneOr404(ctx: RouteContext, fleet: MockFleet) {
    const zone = this.store.state.zones.find((z) => z.id === ctx.params['zoneId'] && z.fleetId === fleet.id);
    if (!zone) notFound();
    return zone;
  }

  private listZones(ctx: RouteContext): MockResponse {
    const fleet = this.dispatchCenterOr409(ctx);
    return data(
      this.store.state.zones
        .filter((z) => z.fleetId === fleet.id)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ fleetId, ...zone }) => zone),
    );
  }

  private saveZone(ctx: RouteContext): MockResponse {
    const fleet = this.dispatchCenterOr409(ctx);
    const existing = ctx.params['zoneId'] ? this.zoneOr404(ctx, fleet) : null;
    const body = { ...(existing ?? {}), ...(ctx.body ?? {}) };
    const name = String(body.name ?? '').trim();
    if (!name) invalid({ name: ['El nombre es obligatorio.', 'Required'] });
    const taken = this.store.state.zones.some(
      (z) => z.fleetId === fleet.id && z.id !== existing?.id && normalize(z.name) === normalize(name),
    );
    if (taken) invalid({ name: ['Ya hay una zona con ese nombre.', 'Unique'] });
    const zone = {
      id: existing?.id ?? this.store.newId(),
      fleetId: fleet.id,
      name,
      polygon: this.validatePolygon(body.polygon, 'polygon'),
      active: body.active !== false,
      updatedAt: nowIso(),
    };
    if (existing) Object.assign(existing, zone);
    else this.store.state.zones.push(zone);
    const { fleetId, ...view } = existing ?? zone;
    return data(view, existing ? 200 : 201);
  }

  private deleteZone(ctx: RouteContext): MockResponse {
    const fleet = this.dispatchCenterOr409(ctx);
    const zone = this.zoneOr404(ctx, fleet);
    this.store.state.zones = this.store.state.zones.filter((z) => z.id !== zone.id);
    return { status: 204, body: null };
  }

  private policyOf(fleet: MockFleet) {
    return (this.store.state.dispatchPolicies[fleet.id] ??= {
      strategy: 'proximity', intraFleetPriority: true, offerTimeoutSeconds: 20,
      standMaxConsecutiveRejections: 2, skipByRequirementsKeepsPosition: true, updatedAt: null, updatedBy: null,
    });
  }

  private updatePolicy(ctx: RouteContext): MockResponse {
    const fleet = this.dispatchCenterOr409(ctx);
    const current = this.policyOf(fleet);
    const body = ctx.body ?? {};
    if ((body.updatedAt ?? null) !== current.updatedAt) {
      conflict('dispatch_policy_outdated', 'Alguien ha cambiado la política de reparto mientras la editabas. Vuelve a cargarla.');
    }
    const errors: Record<string, [string, string]> = {};
    if (!DISPATCH_STRATEGIES.includes(body.strategy)) errors['strategy'] = ['Estrategia no válida.', 'In'];
    const timeout = Number(body.offerTimeoutSeconds);
    if (!(Number.isInteger(timeout) && timeout >= 10 && timeout <= 60)) errors['offer_timeout_seconds'] = ['Entre 10 y 60 segundos.', 'Between'];
    const rejections = Number(body.standMaxConsecutiveRejections);
    if (!(Number.isInteger(rejections) && rejections >= 1 && rejections <= 5)) errors['stand_max_consecutive_rejections'] = ['Entre 1 y 5.', 'Between'];
    if (Object.keys(errors).length) invalid(errors);
    const next = {
      strategy: body.strategy,
      intraFleetPriority: !!body.intraFleetPriority,
      offerTimeoutSeconds: timeout,
      standMaxConsecutiveRejections: rejections,
      skipByRequirementsKeepsPosition: !!body.skipByRequirementsKeepsPosition,
    };
    const fields: [keyof typeof next, string][] = [
      ['strategy', 'strategy'],
      ['intraFleetPriority', 'intra_fleet_priority'],
      ['offerTimeoutSeconds', 'offer_timeout_seconds'],
      ['standMaxConsecutiveRejections', 'stand_max_consecutive_rejections'],
      ['skipByRequirementsKeepsPosition', 'skip_by_requirements_keeps_position'],
    ];
    const changes = fields
      .filter(([key]) => current[key] !== next[key])
      .map(([key, field]) => ({ field, from: current[key], to: next[key] }));
    const actor = { id: 'me', name: 'Tú' };
    Object.assign(current, next, { updatedAt: nowIso(), updatedBy: actor });
    if (changes.length) {
      (this.store.state.policyHistory[fleet.id] ??= []).unshift({ id: this.store.newId(), at: current.updatedAt!, actor, changes });
    }
    return data({ ...current });
  }

  // ---------------------------------------------------------------- cuota al pasajero (fase 4a)

  private sortedRules() {
    const rank = (r: { region: unknown; mode: unknown }) => (r.region ? 2 : 0) + (r.mode ? 1 : 0);
    return [...this.store.state.feeRules].sort(
      (a, b) =>
        rank(a) - rank(b) ||
        (a.region?.name ?? '').localeCompare(b.region?.name ?? '') ||
        SERVICE_TYPES.indexOf(a.serviceType) - SERVICE_TYPES.indexOf(b.serviceType),
    );
  }

  private feeAudit(action: 'rule_created' | 'rule_updated' | 'rule_deleted' | 'region_toggled', target: string, changes: { field: string; from: unknown; to: unknown }[]) {
    this.store.state.feeHistory.push({ id: this.store.newId(), at: nowIso(), actor: { id: 'me', name: 'Tú' }, action, target, changes });
  }

  private ruleLabel(rule: { region: { name: string } | null; mode: string | null; serviceType: string }): string {
    return [rule.region?.name ?? 'Todas', rule.mode ?? 'taxi y vtc', rule.serviceType].join(' · ');
  }

  private saveFeeRule(ctx: RouteContext): MockResponse {
    const rules = this.store.state.feeRules;
    const existing = ctx.params['ruleId'] ? rules.find((r) => r.id === ctx.params['ruleId']) : null;
    if (ctx.params['ruleId'] && !existing) notFound();
    const body = ctx.body ?? {};
    const regionId = existing ? (existing.region?.id ?? null) : (body.regionId ?? null);
    const mode = existing ? existing.mode : (body.mode ?? null);
    const serviceType = (existing ? existing.serviceType : body.serviceType) as ServiceType;
    const errors: Record<string, [string, string]> = {};
    if (regionId !== null && !this.store.state.regions.some((r) => r.id === regionId)) errors['region_id'] = ['La región no existe.', 'Exists'];
    if (mode !== null && !['taxi', 'vtc'].includes(mode)) errors['mode'] = ['Modo no válido.', 'In'];
    if (!SERVICE_TYPES.includes(serviceType)) errors['service_type'] = ['Tipo de servicio no válido.', 'In'];
    const amount = Number(body.amountCents);
    if (!(Number.isInteger(amount) && amount >= 0 && amount <= 1000)) errors['amount_cents'] = ['La cuota va de 0 a 10,00 €.', 'Between'];
    if (Object.keys(errors).length) invalid(errors);
    if (!existing && rules.some((r) => (r.region?.id ?? null) === regionId && r.mode === mode && r.serviceType === serviceType)) {
      conflict('fee_rule_duplicate', 'Ya hay una regla para esa ciudad, modo y tipo de servicio.');
    }
    if (existing) {
      this.feeAudit('rule_updated', this.ruleLabel(existing), [{ field: 'amount_cents', from: existing.amountCents, to: amount }]);
      existing.amountCents = amount;
      existing.updatedAt = nowIso();
      return data(existing);
    }
    const rule = { id: this.store.newId(), region: this.store.region(regionId), mode, serviceType, amountCents: amount, updatedAt: nowIso() };
    rules.push(rule);
    this.feeAudit('rule_created', this.ruleLabel(rule), [{ field: 'amount_cents', from: null, to: amount }]);
    return data(rule, 201);
  }

  private deleteFeeRule(ctx: RouteContext): MockResponse {
    const rule = this.store.state.feeRules.find((r) => r.id === ctx.params['ruleId']);
    if (!rule) notFound();
    if (!rule.region && !rule.mode) {
      conflict('fee_rule_required', 'Las reglas generales no se borran: ponlas a 0 €.');
    }
    this.store.state.feeRules = this.store.state.feeRules.filter((r) => r.id !== rule.id);
    this.feeAudit('rule_deleted', this.ruleLabel(rule), [{ field: 'amount_cents', from: rule.amountCents, to: null }]);
    return { status: 204, body: null };
  }

  private toggleFeeRegion(ctx: RouteContext): MockResponse {
    const row = this.store.state.feeRegions.find((r) => r.region.id === ctx.params['regionId']);
    if (!row) notFound();
    const reason = String(ctx.body?.reason ?? '').trim();
    if (!reason) invalid({ reason: ['Indica el motivo del cambio.', 'Required'] });
    const enabled = !!ctx.body?.feesEnabled;
    if (enabled !== row.feesEnabled) {
      this.feeAudit('region_toggled', row.region.name, [{ field: 'fees_enabled', from: row.feesEnabled, to: enabled }]);
    }
    Object.assign(row, { feesEnabled: enabled, reason, changedAt: nowIso(), changedBy: { name: 'Tú' } });
    return data(row);
  }

  private simulateFee(ctx: RouteContext): MockResponse {
    const body = ctx.body ?? {};
    const errors: Record<string, [string, string]> = {};
    if (!this.store.state.regions.some((r) => r.id === body.regionId)) errors['region_id'] = ['Elige una ciudad.', 'Required'];
    if (!['taxi', 'vtc'].includes(body.mode)) errors['mode'] = ['Elige taxi o VTC.', 'In'];
    if (!DEMAND_CHANNELS.includes(body.demandChannel)) errors['demand_channel'] = ['Canal no válido.', 'In'];
    if (!SERVICE_TYPES.includes(body.serviceType)) errors['service_type'] = ['Tipo de servicio no válido.', 'In'];
    const fare = Number(body.fareCents);
    if (!(Number.isInteger(fare) && fare >= 0)) errors['fare_cents'] = ['El precio no es válido.', 'Min'];
    if (Object.keys(errors).length) invalid(errors);
    return data(resolvePassengerFee(this.store.state.feeRules, regionsEnabled(this.store.state), { ...body, fareCents: fare }));
  }

  // ---------------------------------------------------------------- retos (fase 4a)

  /** Estado según las fechas (salvo borrador y cancelado, que se guardan). */
  private challengeView(challenge: DriverChallenge): DriverChallenge {
    if (challenge.status === 'draft' || challenge.status === 'cancelled') return challenge;
    const now = Date.now();
    const status = now < Date.parse(challenge.startsAt) ? 'scheduled' : now > Date.parse(challenge.endsAt) ? 'finished' : 'active';
    return { ...challenge, status };
  }

  private challengeOr404(ctx: RouteContext): DriverChallenge {
    const challenge = this.store.state.challenges.find((c) => c.id === ctx.params['challengeId']);
    if (!challenge) notFound();
    return this.challengeView(challenge);
  }

  private storedChallenge(ctx: RouteContext): DriverChallenge {
    this.challengeOr404(ctx);
    return this.store.state.challenges.find((c) => c.id === ctx.params['challengeId'])!;
  }

  private listChallenges(ctx: RouteContext): MockResponse {
    const { query } = ctx;
    const scoped = this.store.state.challenges
      .map((c) => this.challengeView(c))
      .filter(
        (c) =>
          (!query.get('region_id') || c.region?.id === query.get('region_id')) &&
          includesText([c.title, c.description], query.get('search')),
      );
    const counts = countBy(scoped, (c) => c.status);
    CHALLENGE_STATUSES.forEach((status) => (counts[status] ??= 0));
    const rows = scoped
      .filter((c) => !query.get('status') || c.status === query.get('status'))
      .sort((a, b) => b.startsAt.localeCompare(a.startsAt));
    return paginate(rows, query, counts);
  }

  private saveChallenge(ctx: RouteContext): MockResponse {
    const existing = ctx.params['challengeId'] ? this.storedChallenge(ctx) : null;
    if (existing && !['draft', 'scheduled'].includes(this.challengeView(existing).status)) {
      conflict('challenge_locked', 'Este reto ya ha empezado y no se puede cambiar.');
    }
    const body = { ...(ctx.body ?? {}) };
    const errors: Record<string, [string, string]> = {};
    const title = String(body.title ?? existing?.title ?? '').trim();
    if (!title) errors['title'] = ['El título es obligatorio.', 'Required'];
    const regionId = 'regionId' in body ? body.regionId : (existing?.region?.id ?? null);
    if (regionId && !this.store.state.regions.some((r) => r.id === regionId)) errors['region_id'] = ['La región no existe.', 'Exists'];
    const mode = 'mode' in body ? body.mode : (existing?.mode ?? null);
    const startsAt = String(body.startsAt ?? existing?.startsAt ?? '');
    const endsAt = String(body.endsAt ?? existing?.endsAt ?? '');
    if (!startsAt || Number.isNaN(Date.parse(startsAt))) errors['starts_at'] = ['La fecha de inicio no es válida.', 'Date'];
    if (!endsAt || Number.isNaN(Date.parse(endsAt)) || Date.parse(endsAt) <= Date.parse(startsAt)) errors['ends_at'] = ['El fin tiene que ser posterior al inicio.', 'After'];
    const goals = { minTrips: null, minHoursOnline: null, minFareCents: null, minRating: null, ...(existing?.goals ?? {}), ...(body.goals ?? {}) };
    if (!Object.values(goals).some((v) => v !== null && v !== undefined && v !== '')) errors['goals'] = ['Pon al menos un objetivo.', 'Required'];
    const bonus = Number(body.bonusCents ?? existing?.bonusCents);
    if (!(Number.isInteger(bonus) && bonus >= 100 && bonus <= 50000)) errors['bonus_cents'] = ['El bono va de 1 a 500 €.', 'Between'];
    if (Object.keys(errors).length) invalid(errors);
    const values = {
      title,
      description: body.description !== undefined ? body.description || null : (existing?.description ?? null),
      region: this.store.region(regionId),
      mode,
      startsAt,
      endsAt,
      goals,
      bonusCents: bonus,
    };
    if (existing) {
      Object.assign(existing, values);
      return data(this.challengeView(existing));
    }
    const challenge: DriverChallenge = {
      id: this.store.newId(),
      ...values,
      status: 'draft',
      counts: { participants: 0, completed: 0 },
      bonusPaidCents: 0,
      createdAt: nowIso(),
    };
    this.store.state.challenges.push(challenge);
    this.store.state.challengeParticipants[challenge.id] = [];
    return data(challenge, 201);
  }

  private deleteChallenge(ctx: RouteContext): MockResponse {
    const challenge = this.storedChallenge(ctx);
    if (challenge.status !== 'draft') conflict('challenge_locked', 'Solo se pueden borrar los retos en borrador.');
    this.store.state.challenges = this.store.state.challenges.filter((c) => c.id !== challenge.id);
    return { status: 204, body: null };
  }

  private publishChallenge(ctx: RouteContext): MockResponse {
    const challenge = this.storedChallenge(ctx);
    if (challenge.status !== 'draft') conflict('challenge_locked', 'Este reto ya está publicado.');
    if (Date.parse(challenge.endsAt) < Date.now()) conflict('challenge_locked', 'El reto ya ha terminado: cambia las fechas antes de publicarlo.');
    challenge.status = 'scheduled';
    return data(this.challengeView(challenge));
  }

  private cancelChallenge(ctx: RouteContext): MockResponse {
    const challenge = this.storedChallenge(ctx);
    const status = this.challengeView(challenge).status;
    if (!['scheduled', 'active'].includes(status)) conflict('challenge_locked', 'Este reto no se puede cancelar.');
    challenge.status = 'cancelled';
    return data(challenge);
  }
}

/** Ruta relativa a `/backoffice` (`/fleets/…`), o `null` si la URL no es del backoffice. */
function backofficePath(url: string): string | null {
  const clean = url.split('?')[0];
  const index = clean.indexOf('/backoffice/');
  if (index === -1) {
    return null;
  }
  return clean.slice(index + '/backoffice'.length).replace(/\/$/, '');
}

/** PDF mínimo de una página con unas líneas de texto (solo para probar la descarga). */
function simplePdf(title: string, lines: string[]): Blob {
  const escape = (text: string) => text.replace(/[\\()]/g, (c) => `\\${c}`).replace(/[^\x20-\x7e]/g, '?');
  const textLines = [title, '', ...lines].map((line, index) => `BT /F1 ${index === 0 ? 14 : 9} Tf 40 ${800 - index * 16} Td (${escape(line)}) Tj ET`);
  const content = textLines.join('\n');
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >>',
    `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
  ];
  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });
  const xref = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
  offsets.forEach((offset) => (pdf += `${String(offset).padStart(10, '0')} 00000 n \n`));
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;
  return new Blob([pdf], { type: 'application/pdf' });
}

/** `YYYY-MM-DD` en hora local. */
function localDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** Nombre corto de cada documento para «Necesita tu atención» (el backend lo manda ya traducido). */
const DOC_NAME: Partial<Record<DocumentType, string>> = {
  bank_ownership_certificate: 'Certificado bancario',
  route_sheet_authorization: 'Apoderamiento de hojas de ruta',
  circulation_permit: 'Permiso de circulación',
  technical_sheet_itv: 'ITV',
  insurance_policy: 'Seguro',
  taxi_municipal_license: 'Licencia de taxi',
  vtc_transport_card: 'Tarjeta VTC',
  driving_license: 'Carnet de conducir',
  taxi_driver_card: 'Carnet de autotaxi',
  vtc_driver_permit: 'Permiso de conductor VTC',
  social_security_registration: 'Alta en la Seguridad Social',
};
